import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Plus, ArrowLeft, Edit, Trash2, Shield, Save, X,
  ChevronDown, ChevronUp, Building2, Check, Lock, Star, Search, UserCog, UserCheck, UserX,
  BarChart2, Eye, LogIn, User2, Activity
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { MODULOS_UNICOS } from "../lib/moduloUnico";
import { DEFAULT_EMPRESA_MODULOS, PERMISO_A_MODULO_EMPRESA, EMPRESA_MODULOS, modulosHabilitadosEmpresa } from "../lib/modulosEmpresa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grupos de permisos que tienen control de scope de movimientos
const GRUPOS_CON_SCOPE = new Set(["ventas_base", "presupuestos", "ingresos_varios", "egresos_base", "financiero"]);
const GRUPOS_SCOPE_LABELS = {
  ventas_base: "Ventas base",
  presupuestos: "Presupuestos",
  ingresos_varios: "Ingresos sin factura",
  egresos_base: "Egresos base",
  financiero: "Financiero",
};

const MODULOS_LABELS = {
  entidades: "Acciones de registro",
  empresas: "Clientes",
  configuracion_catastro: "Configuración",
  presupuestos: "Presupuestos",
  inventario: "Inventario",
  configuracion_inventario: "Config. Inventario",
  credenciales: "Credenciales",
  reportes: "Reportes",
  alertas: "Alertas",
  costos: "Costos",
  proveedores: "Proveedores",
  costos_fijos: "Costos fijos",
  pago_iva: "Pago de IVA (SET)",
  empleados: "Empleados",
  sueldos: "Sueldos",
  facturas: "Facturas",
  movimientos: "Movimientos",
  flow_facturaciones: "Flow de Facturaciones",
  flow_financieros: "Flow de Financieros",
  balance: "Balance",
  ingresos_varios: "Ingresos varios",
  pagos_proveedores: "Pagos a proveedores",
  compras: "Compras",
  recibos: "Recibos",
  configuracion_ventas: "Configuraciones",
  pdv: "PDV",
  notas_credito: "Notas de crédito",
  inventario_productos: "Inventario productos",
  historial_stock: "Historial de stock",
  historial_costos: "Historial de costos",
  configuracion_productos: "Config. Productos",
  bancos: "Bancos",
  plan_cuentas: "Plan de cuentas",
  usuarios: "Usuarios",
  auditoria: "Auditoría",
  configuracion_global: "Configuración global",
  soporte: "Soporte",
  avisos: "Avisos",
  transferencias: "Transferencias",
  configuracion_financiero: "Configuración financiero",
  configuracion_egresos: "Configuración egresos",
  anticipos_proveedores: "Anticipos a proveedores",
  cotizaciones: "Cotizaciones",
  lectura_bombas: "Lectura de Bombas",
  tipos_factura: "Tipos de factura",
  produccion: "Producción",
  produccion_kds: "Cocina / KDS",
  produccion_facturacion: "Facturación producción",
  produccion_historial: "Historial producción",
  produccion_fichas: "Fichas técnicas",
  produccion_configuracion: "Configuración producción",
  hoteleria: "Hotelería",
  loteadora: "Loteadora",
  cheques: "Emisión de Cheques",
  prestamos: "Préstamos",
  backup: "BackUp de datos",
  licencias: "Licencias",
  vendedores: "Vendedores",
  restaurante: "Restaurante",
  clinica_agenda: "Clínica — Agenda de turnos",
  clinica_pacientes: "Clínica — Pacientes",
  clinica_expediente: "Clínica — Expediente clínico",
  clinica_configuracion: "Clínica — Configuración",
  clinica_liquidaciones: "Clínica — Liquidación de comisiones",
  clinica_reportes: "Clínica — Reportes",
  constructora_proyectos: "Constructora — Proyectos",
  constructora_configuracion: "Constructora — Configuración",
  constructora_presupuesto: "Constructora — Presupuesto / cómputo métrico",
  constructora_cronograma: "Constructora — Cronograma y clima",
  constructora_anticipos: "Constructora — Anticipos de obra",
  constructora_compras: "Constructora — Compras de la obra",
  constructora_gastos: "Constructora — Gastos de la obra",
  constructora_equipos: "Constructora — Equipos y maquinarias",
  constructora_personal: "Constructora — Personal de obra",
  constructora_facturacion: "Constructora — Facturación de la obra",
  constructora_reportes: "Constructora — Reportes",
};

const ACCIONES_LABELS = {
  ver: "Ver",
  crear: "Crear",
  editar: "Editar",
  eliminar: "Eliminar",
  exportar: "Exportar",
  afectar_stock: "Cambiar afectación stock",
  ajustar_stock: "Ajustar stock",
  transferir_stock: "Transferir mercadería",
  ver_depositos: "Ver depósitos",
  gestionar_depositos: "Gestionar depósitos",
  transferir_depositos: "Transferir entre depósitos",
  anular_transferencia_deposito: "Anular transferencia entre depósitos",
  ver_stock_madre: "Ver stock de la empresa madre",
  ver_stock_sucursales: "Ver stock de sucursales",
  crear_servicio: "Crear servicios",
  stock_inicial: "Stock inicial",
  desvincular: "Desvincular",
  modo_libre: "Modo libre",
  caja_banco: "Caja / Banco",
  movimientos: "Movimientos",
  descuentos: "Descuentos",
  kardex: "Ficha Mercadería",
  cuentas_cobrar: "Cuentas por cobrar",
  cuentas_pagar: "Cuentas por pagar",
  estado_cuenta: "Estado de Cuenta",
  asignar_cuentas: "Asignar acceso a cuentas",
  gestionar_cuotas: "Gestionar cuotas",
  traspasar_saldos: "Traspaso de Saldos (entre cuentas del plan)",
  ver_config: "Ver configuración de retenciones",
  editar_config: "Editar configuración de retenciones",
  gestionar_constancias: "Gestionar constancias de proveedores",
  omitir_retencion: "Omitir la retención al registrar un pago",
  excluir: "Excluir / incluir en la programación",
  generar_lote: "Generar e imprimir orden de pago",
  anular: "Anular",
  cancelar: "Cancelar",
  cobro_por_items: "Cobro por ítems",
  ver_todos: "Ver todos los tickets",
  responder: "Responder tickets",
  gestionar: "Gestionar tickets",
  historial: "Ver historial",
  gestionar_acceso_transferencias: "Gestionar acceso transferencias",
  gestionar_acceso_bancos: "Gestionar acceso bancos",
  gestionar_fechas_movimientos: "Configurar restricción de fechas",
  crear_empleado: "Crear préstamo a empleado",
  crear_proveedor: "Crear préstamo a proveedor",
  crear_bancario: "Crear préstamo bancario",
  editar_en_ventas: "Editar TC en ventas",
  editar_en_compras: "Editar TC en compras",
  editar_completa: "Editar completa",
  editar_precio_venta: "Fijar precio de venta",
  configurar_impresion: "Configurar impresión",
  modificar_precio_abajo: "Bajar precio",
  modificar_precio_arriba: "Subir precio",
  agregar_nota: "Agregar nota interna",
  editar_nota: "Editar / eliminar nota",
  apertura_turno: "Apertura de turno",
  cierre_turno: "Cierre de turno",
  configurar: "Configurar",
  reportes: "Reportes",
  eliminar: "Eliminar registro",
  asignar_tipo: "Asignar tipo",
  editar_tipo: "Cambiar tipo",
  filtrar_tipo_factura: "Filtrar por tipo",
  registrar_chequera: "Registrar talonario",
  editar_chequera: "Editar talonario",
  emitir: "Emitir cheque",
  descontar: "Descontar cheque",
  liberar: "Liberar cheque",
  eliminar_chequera: "Eliminar talonario",
  db_exportar: "Exportar backup",
  db_restaurar: "Restaurar backup",
  gestionar_sueldos: "Gestionar sueldos",
  pagar: "Pagar",
  gestionar_adelantos: "Gestionar adelantos",
  gestionar_extras: "Gestionar extras",
  deshacer_pago: "Deshacer pago",
  ajustar_sueldo: "Ajustar sueldo",
  operar: "Operar",
  configurar: "Configurar",
  cambiar_estado: "Cambiar estado",
  imprimir_orden: "Imprimir orden de carga",
};

// Nombres específicos por permiso completo (modulo.accion) — sobreescribe ACCIONES_LABELS cuando aplica
const PERMISOS_NOMBRES = {
  "cheques.configurar_impresion": "Configurar impresión de cheques",
  "facturas.editar": "Solo pagar",
  "cotizaciones.gestionar": "Gestionar",
  "configuracion_ventas.gestionar_descuentos": "Gestionar descuentos",
  "configuracion_ventas.gestionar_recibos": "Configurar modos de recibo",
  "configuracion_ventas.gestionar_timbrado": "Gestionar timbrado fiscal",
  "configuracion_ventas.ver_huecos_timbrado": "Ver y reutilizar huecos de numeración",
  "configuracion_ventas.gestionar_delivery": "Configurar Delivery: productos de delivery, formato de la orden y cuándo se imprime",
  "facturas.imprimir_delivery": "Cargar los datos de entrega e imprimir la Orden de Delivery en facturas con producto de delivery",
  "configuracion_ventas.gestionar_cobro_contado": "Configurar el cobro automático de ventas al contado (por empresa): al registrar la venta queda cobrada sin pagar después",
  "configuracion_ventas.gestionar_impresion": "Configurar impresión de facturas: impresión automática al guardar y qué modelos de impresión (A4, doble vía, compacta, ticket) están habilitados para la empresa",
  "pdv.ver": "Ver PDV",
  "pdv.operar": "Operar caja",
  "pdv.configurar": "Configurar PDV",
  "reportes.descuentos": "Desc. en productos",
  "reportes.vencimiento_productos": "Vencimiento de productos",
  "reportes.mercaderias_pendientes": "Mercaderías pendientes (por despachar / por recibir)",
  "entregas.ver": "Ver entregas pendientes y recepciones",
  "entregas.crear": "Preparar y confirmar entregas / recepciones",
  "entregas.anular": "Anular entregas / recepciones confirmadas",
  "entregas.configurar": "Configurar entrega posterior por defecto",
  "reportes.libro_ventas": "Libro Ventas",
  "reportes.comisiones_vendedores": "Comisiones de Vendedores",
  "reportes.gastos_categoria": "Gastos por Categoría",
  "reportes.retenciones": "Retenciones de IVA",
  "reportes.costo_hectarea": "Costo por Hectárea",
  "reportes.transporte": "Transporte — Clientes (fletes)",
  "reportes.transporte_chofer": "Transporte — Choferes (comisión, faltante, gastos)",
  "ingresos_varios.crear_vale": "Cargar vales de cliente",
  "reportes.catastros": "Catastro unificado",
  "reportes.balancete_obra": "Balancete - Obra",
  "reportes.balance_gerencial": "Balance Gerencial",
  "reportes.garantias": "Garantías",
  "garantias.ver": "Ver garantías",
  "garantias.emitir": "Emitir / editar pagaré",
  "garantias.cobrar": "Cobrar garantía",
  "garantias.anular": "Anular / destruir documento",
  "licencias.autoextender": "Autoextender licencia vencida (+2 días, 1 vez)",
  "presupuestos.ver_costo_compra": "Ver costo/% ganancia",
  "inventario_productos.gestionar_descuentos": "Gestionar descuentos",
  "inventario_productos.editar_costo": "Editar costo del producto",
  "inventario_productos.generar_etiquetas": "Generar etiquetas",
  "inventario_productos.generar_codigo_barras": "Generar código de barras",
  "inventario_productos.editar_codigo_barras": "Editar código de barras",
  "inventario_productos.eliminar_codigo_barras": "Eliminar código de barras",
  "configuracion_productos.configurar_codigos_barras": "Configurar códigos de barras",
  "configuracion_productos.ver_disenos_etiqueta": "Ver diseños de etiqueta",
  "configuracion_productos.crear_disenos_etiqueta": "Crear diseños de etiqueta",
  "configuracion_productos.editar_disenos_etiqueta": "Editar diseños de etiqueta",
  "configuracion_productos.eliminar_disenos_etiqueta": "Eliminar diseños de etiqueta",
  "configuracion_productos.gestionar_atributos": "Gestionar atributos",
  "configuracion_productos.habilitar_clasificaciones": "Habilitar clasificaciones",
  "configuracion_productos.gestionar_unicidad": "Gestionar nombres duplicados",
  "configuracion_inventario.gestionar_unicidad": "Gestionar nombres duplicados",
  "historial_costos.ver": "Ver historial costos",
  "produccion_kds.tomar_orden": "Tomar orden",
  "produccion_kds.avanzar": "Avanzar KDS",
  "produccion_kds.eliminar": "Eliminar orden",
  "produccion_kds.imprimir": "Imprimir pedido",
  "produccion_kds.etapa_a_recibir": "Etapa: A recibir",
  "produccion_kds.etapa_en_preparacion": "Etapa: En preparación",
  "produccion_kds.etapa_listo": "Etapa: Listo",
  "produccion_kds.etapa_despachado": "Etapa: Despachado",
  "produccion_facturacion.boleta": "Emitir boleta",
  "produccion_facturacion.factura_legal": "Emitir factura legal",
  "produccion_fichas.producir_stock": "Producir stock",
  "produccion_configuracion.catalogos": "Gestionar catálogos",
  "produccion_configuracion.categorias": "Gestionar categorías",
  "produccion_configuracion.variantes": "Gestionar variantes",
  "produccion_configuracion.adicionales": "Gestionar adicionales",
  "produccion_configuracion.delivery": "Gestionar delivery",
  "produccion_configuracion.modos": "Gestionar modos",
  // Hotelería — nombres específicos para pills más descriptivos
  "hoteleria.ver_panel": "Ver panel",
  "hoteleria.checkin": "Check-In",
  "hoteleria.checkout": "Check-Out",
  "hoteleria.reserva": "Crear reserva",
  "hoteleria.editar_reserva": "Editar reserva",
  "hoteleria.cancelar_reserva": "Cancelar reserva",
  "hoteleria.registrar_gasto": "Registrar consumo",
  "hoteleria.ver_extracto": "Ver cuenta",
  "hoteleria.crear_hab": "Crear habitación",
  "hoteleria.editar_hab": "Editar habitación",
  "hoteleria.eliminar_hab": "Eliminar habitación",
  "hoteleria.reportes": "Histórico reservas",
  "hoteleria.registrar_anticipo": "Registrar anticipo",
  "hoteleria.ver_anticipos": "Ver anticipos",
  "hoteleria.ver_huespedes": "Ver huéspedes",
  "hoteleria.editar_huespedes": "Editar huéspedes",
  "hoteleria.eliminar_huespedes": "Eliminar huéspedes",
  // Restaurante — nombres específicos para acciones que no están en ACCIONES_LABELS
  "restaurante.cerrar_cuenta": "Liberar tarjeta (cobrar)",
  "restaurante.generar_tarjetas": "Generar / cargar tarjetas",
  "restaurante.liberar_sin_cobrar": "Liberar mesa SIN cobrar",
  "restaurante.cocina": "Pantalla Cocina",
  "restaurante.disponibilidad": "Disponibilidad de platos",
  "restaurante.mozo": "Pantalla Mozos",
  "restaurante.configurar": "Configurar menú de cocina",
  "transporte.liquidar": "Liquidar flete al chofer",
  "importaciones.liquidar": "Registrar despacho y liquidar",
  "importaciones.nacionalizar": "Nacionalizar (ingresar a stock)",
  "importaciones.reliquidar": "Reliquidar costos",
  "importaciones.configurar": "Configurar aranceles y tasas",
  "importaciones.ver_costos": "Ver costos e impuestos",
  "bascula.configurar_descuentos": "Configurar tipos de descuento",
  "bascula.capturar_peso_equipo": "Capturar peso desde la balanza",
  "equipos_bascula.probar_captura": "Probar lectura de la balanza",
  "equipos_bascula.gestionar_catalogos": "Gestionar catálogo de modelos",
  "recetario.gestionar_ingenieros": "Gestionar ingenieros agrónomos",
  "recetario.gestionar_chacras": "Gestionar chacras de clientes",
  "clinica_agenda.ver": "Ver agenda",
  "clinica_agenda.crear": "Crear turnos",
  "clinica_agenda.editar": "Editar / reprogramar turnos",
  "clinica_agenda.cancelar": "Cancelar turnos",
  "clinica_agenda.atender": "Marcar turno atendido",
  "clinica_agenda.facturar": "Facturar turno",
  "clinica_agenda.asistencia": "Registrar asistencia (huella digital)",
  "clinica_pacientes.ver": "Ver pacientes",
  "clinica_pacientes.crear": "Crear pacientes",
  "clinica_pacientes.editar": "Editar pacientes",
  "clinica_pacientes.eliminar": "Eliminar pacientes",
  "clinica_expediente.ver": "Ver expediente clínico",
  "clinica_expediente.escribir": "Cargar indicaciones y evoluciones",
  "clinica_expediente.archivos": "Subir estudios / archivos",
  "clinica_expediente.eliminar": "Eliminar registros del expediente",
  "clinica_expediente.certificados": "Emitir certificado e indicaciones/receta (con firma)",
  "clinica_configuracion.ver": "Ver configuración",
  "clinica_configuracion.profesionales": "Gestionar profesionales",
  "clinica_configuracion.servicios": "Gestionar servicios",
  "clinica_configuracion.comisiones": "Gestionar comisiones",
  "clinica_liquidaciones.ver": "Ver liquidaciones",
  "clinica_liquidaciones.generar": "Generar liquidación",
  "clinica_liquidaciones.pagar": "Registrar pago de comisión",
  "clinica_liquidaciones.anular": "Anular liquidación",
  "clinica_reportes.ver": "Ver reportes de la clínica",
  "inventario_productos.marcar_receta": "Marcar \"requiere receta\" y ficha agronómica",
  "constructora_proyectos.contrato_ver": "Ver / descargar contrato",
  "constructora_proyectos.contrato_cargar": "Cargar / eliminar contrato",
  "constructora_proyectos.plan_cobros": "Editar plan de cobros",
  "constructora_proyectos.cambiar_estado": "Cambiar estado del proyecto",
  "constructora_configuracion.gestionar_grupos": "Gestionar grupos de compra/gasto",
  "constructora_configuracion.gestionar_unidades": "Gestionar unidades de medida",
  "constructora_presupuesto.aprobar": "Aprobar / rechazar presupuesto",
  "constructora_presupuesto.copiar": "Copiar presupuesto",
  "constructora_presupuesto.ver_costos": "Ver costos y sub-ítems",
  "constructora_presupuesto.vincular_factura": "Usar el presupuesto para facturar avances",
  "constructora_cronograma.reprogramar": "Reprogramar fechas del cronograma",
  "constructora_cronograma.clima": "Registrar días de lluvia / clima",
  "constructora_equipos.asignar": "Asignar equipos a la obra",
  "constructora_personal.asignar": "Asignar personal a la obra",
  "constructora_personal.pagar": "Registrar pagos y recibos del personal",
  "constructora_facturacion.facturar": "Emitir facturas/boletas desde el proyecto y aplicar anticipos",
  "constructora_anticipos.anular": "Anular un anticipo no aplicado",
};

// Subgrupos visuales dentro de un mismo módulo (sin cambiar los strings de permiso)
const PERM_SUBGROUPS = {
  hoteleria: [
    { label: "Panel",       acciones: ["ver_panel", "checkin", "checkout"] },
    { label: "Reservas",    acciones: ["reserva", "editar_reserva", "cancelar_reserva", "registrar_gasto", "ver_extracto", "registrar_anticipo", "ver_anticipos"] },
    { label: "Habitaciones",acciones: ["crear_hab", "editar_hab", "eliminar_hab"] },
    { label: "Histórico",   acciones: ["reportes"] },
    { label: "Huéspedes",   acciones: ["ver_huespedes", "editar_huespedes", "eliminar_huespedes"] },
  ],
  loteadora: [
    { label: "Consulta",       acciones: ["ver"] },
    { label: "Lotes",          acciones: ["gestionar_lotes"] },
    { label: "Reservas",       acciones: ["reservar"] },
    { label: "Ventas",         acciones: ["gestionar_ventas"] },
    { label: "Cobros",         acciones: ["cobrar_cuotas"] },
    { label: "Catastro SNC",   acciones: ["ver_catastro"] },
    { label: "Configuración",  acciones: ["configurar"] },
  ],
  configuracion_global: [
    { label: "Ventana de Configuración global", acciones: ["ver", "editar"] },
  ],
  auditoria: [
    { label: "Módulo Auditoría",  acciones: ["ver"] },
    { label: "Info de registros", acciones: ["ver_info_registro"] },
    { label: "Forzar logout",     acciones: ["forzar_logout"] },
  ],
  prestamos: [
    { label: "Consulta", acciones: ["ver"] },
    { label: "Creación", acciones: ["crear_empleado", "crear_proveedor", "crear_bancario"] },
    { label: "Cuotas", acciones: ["pagar", "cuota_manual"] },
    { label: "Descuentos", acciones: ["descuento_empleado_interes", "descuento_empleado_extra", "descuento_proveedor", "descuento_bancario"] },
    { label: "Gestión", acciones: ["cancelar"] },
  ],
  sueldos: [
    { label: "Consulta", acciones: ["ver"] },
    { label: "Pagos", acciones: ["pagar", "deshacer_pago"] },
    { label: "Conceptos", acciones: ["gestionar_adelantos", "gestionar_extras"] },
    { label: "Gestión", acciones: ["ajustar_sueldo", "liquidar"] },
  ],
  produccion: [
    { label: "Acceso", acciones: ["ver"] },
  ],
  produccion_kds: [
    { label: "Consulta", acciones: ["ver"] },
    { label: "Órdenes", acciones: ["tomar_orden", "avanzar", "eliminar", "imprimir"] },
    { label: "Etapas visibles (opcional — restringe a solo estas columnas)", acciones: ["etapa_a_recibir", "etapa_en_preparacion", "etapa_listo", "etapa_despachado"] },
  ],
  produccion_facturacion: [
    { label: "Comprobantes", acciones: ["boleta", "factura_legal"] },
  ],
  produccion_historial: [
    { label: "Historial", acciones: ["ver"] },
  ],
  produccion_fichas: [
    { label: "Consulta", acciones: ["ver"] },
    { label: "Gestión", acciones: ["crear", "editar", "eliminar", "producir_stock"] },
  ],
  produccion_configuracion: [
    { label: "Acceso", acciones: ["ver"] },
    { label: "Catálogos", acciones: ["categorias", "variantes", "adicionales", "catalogos"] },
    { label: "Parámetros", acciones: ["delivery", "modos"] },
  ],
};

// Tooltips descriptivos para cada permiso (modulo.accion)
const PERMISO_DESC = {
  // Clientes
  "empresas.ver": "Ver listado y detalle de clientes",
  "empresas.crear": "Crear nuevos clientes",
  "empresas.editar": "Editar datos de clientes",
  "empresas.eliminar": "Eliminar clientes",
  "localidades.ver": "Ver catálogo de países, departamentos y ciudades",
  "localidades.crear": "Agregar países, departamentos y ciudades",
  "localidades.editar": "Editar nombres de localidades",
  "localidades.eliminar": "Eliminar localidades del catálogo",
  // Presupuestos
  "presupuestos.ver": "Ver listado y detalle de presupuestos",
  "presupuestos.crear": "Crear nuevos presupuestos",
  "presupuestos.editar": "Editar presupuestos existentes",
  "presupuestos.eliminar": "Eliminar presupuestos",
  "presupuestos.desvincular": "Desvincular una factura de un presupuesto ya vinculado",
  "presupuestos.modo_libre": "Crear presupuestos sin vincular obligatoriamente a un cliente",
  "presupuestos.descuentos_globales": "Aplicar un descuento global (monto o %) sobre el total del presupuesto, además del descuento por ítem",
  "presupuestos.aprobar_parcial": "Aprobar presupuestos total o parcialmente (cantidad por renglón) y ver el histórico de aprobaciones y facturación",
  "presupuestos.recepcion_ver": "Ver el listado de Recepción de Equipos (ingresos de aparatos para mantenimiento)",
  "presupuestos.recepcion_crear": "Registrar el ingreso de un aparato para mantenimiento (genera automáticamente un Presupuesto en borrador)",
  "presupuestos.recepcion_editar": "Editar el diagnóstico técnico y el estado de un aparato en Recepción de Equipos",
  "presupuestos.recepcion_configurar": "Configurar el texto legal que se imprime en la boleta de Recepción de Equipos",
  // Costos (sub-módulo de Presupuestos)
  "costos.ver": "Ver el desglose de costos de materiales y mano de obra dentro de presupuestos",
  "costos.editar": "Editar los costos estimados dentro de un presupuesto",
  // Ventas - Facturas
  "facturas.ver": "Ver listado y detalle de facturas emitidas",
  "facturas.crear": "Crear nuevas facturas de venta",
  "facturas.editar": "Habilita el botón Pagar en la lista de ventas (solo registrar pago, no editar contenido)",
  "facturas.modificar_precio_abajo": "Puede reducir el precio de catálogo al facturar (descuento). Sin este permiso el precio queda bloqueado hacia abajo.",
  "facturas.modificar_precio_arriba": "Puede aumentar el precio de catálogo al facturar. Sin este permiso el precio queda bloqueado hacia arriba.",
  "presupuestos.modificar_precio_abajo": "Puede reducir el precio de catálogo al presupuestar. Sin este permiso el precio queda bloqueado hacia abajo.",
  "presupuestos.modificar_precio_arriba": "Puede aumentar el precio de catálogo al presupuestar. Sin este permiso el precio queda bloqueado hacia arriba.",
  "presupuestos.ver_costo_compra": "Ver el costo de compra y el % de ganancia al presupuestar (modo catálogo). Permiso independiente del de Productos: se puede tener uno sin el otro.",
  "facturas.eliminar": "Eliminar facturas",
  "facturas.anular": "Anular facturas emitidas",
  "facturas.modo_libre": "Crear facturas sin vincular a cliente o presupuesto",
  "facturas.afectar_stock": "Las facturas pueden descontar stock del inventario al emitirse",
  "facturas.cobro_por_items": "Cobrar factura seleccionando ítems individuales (modo restaurante)",
  "facturas.agregar_nota": "Agregar notas al hilo de una factura ya creada. Las notas no se imprimen, solo se ven en la vista previa del sistema.",
  "facturas.editar_nota": "Eliminar entradas del hilo de notas de una factura. Sin este permiso las notas solo se pueden agregar, no borrar.",
  "facturas.asignar_tipo": "Asignar un tipo de factura a una factura que todavía no tiene tipo asignado (primera asignación).",
  "facturas.editar_tipo": "Cambiar el tipo de factura en una factura que ya tiene un tipo asignado.",
  "facturas.descuento_pago": "Otorgar un descuento al registrar un pago/cobro de factura (p. ej. quitar el interés al cancelar todas las cuotas de una vez)",
  "facturas.descuentos_globales": "Aplicar un descuento global (monto o %) sobre el total de la factura o boleta, además del descuento por ítem",
  "facturas.cambiar_punto_expedicion": "Elegir libremente el punto de expedición al facturar. Sin este permiso, si el usuario tiene un punto asignado en Configuración de Timbrado, solo puede facturar con ese punto (el selector queda fijo).",
  "facturas.editar_vendedor_pendiente": "Agregar o corregir el vendedor de una factura si se olvidó al vender — solo mientras la factura siga pendiente (no pagada, no anulada, comisión sin liquidar). Se ofrece usuarios y vendedores del Catastro; queda en auditoría y alinea las notas de crédito vinculadas",
  "facturas.reasignar_vendedor": "Corregir el vendedor de una factura ya cargada (por defecto el vendedor es siempre quien la registra). Todo cambio queda registrado en la auditoría de la factura. Pensado para jefes/supervisores que corrigen errores de carga.",
  // Tipos de factura
  "tipos_factura.gestionar": "Crear, editar y eliminar los tipos de factura configurables (categorías como Proyecto, Garantía, Mantenimiento).",
  // Recibos
  "recibos.ver": "Ver recibos de cobro de facturas a crédito",
  "recibos.crear": "Registrar cobros de facturas a crédito",
  "recibos.editar": "Editar recibos existentes",
  "recibos.mover_pagos": "Mover pagos entre recibos",
  "recibos.anular": "Anular recibos emitidos",
  "recibos.eliminar": "Eliminar recibos sin pagos vinculados",
  // Notas de crédito
  "notas_credito.ver": "Ver notas de crédito emitidas",
  "notas_credito.crear": "Crear notas de crédito",
  "notas_credito.editar": "Editar notas de crédito",
  "notas_credito.eliminar": "Eliminar notas de crédito",
  // Ingresos varios
  "ingresos_varios.ver": "Ver ingresos directos registrados sin factura",
  "ingresos_varios.crear": "Registrar ingresos directos sin emitir factura",
  "ingresos_varios.editar": "Editar ingresos varios",
  "ingresos_varios.eliminar": "Eliminar ingresos varios",
  "ingresos_varios.crear_vale": "Cargar un ingreso como Vale (crédito de cliente) — le da al cliente saldo para usar en compras futuras",
  // Compras
  "compras.ver": "Ver compras y egresos registrados",
  "compras.crear": "Registrar nuevas compras o egresos",
  "compras.editar": "Cargar la factura de una compra ya registrada (número, timbrado, vigencia) — no incluye editar ítems, proveedor ni montos",
  "compras.editar_completa": "Editar por completo una compra ya guardada (proveedor, ítems, montos) — permite guardar una compra y seguir cargándola después. Ajusta el stock automáticamente si la compra ya afectó inventario. Se bloquea si la compra ya tiene factura o pagos registrados.",
  "compras.configurar_impresion": "Permite elegir qué modelos de impresión (A4 / Ticket) se ofrecen al imprimir una Compra",
  "compras.editar_precio_venta": "Al cargar una compra, permite fijar el Precio de Venta de cada producto directamente (además del Costo) — actualiza el precio en Productos al instante, listo para usarse en la próxima Factura",
  "compras.vincular_presupuesto": "Vincular o desvincular un presupuesto a una compra",
  "compras.eliminar": "Eliminar compras",
  "compras.afectar_stock": "Las compras pueden sumar unidades al stock del inventario",
  "compras.modo_libre": "Permite registrar ítems de compra en forma libre (sin requerir producto del catálogo), usando el toggle Catálogo/Libre en el formulario",
  "compras.descuentos": "Permite aplicar descuentos en compras. Actualmente habilita el descuento global del documento y queda preparado para descuentos por ítem.",
  // Costos fijos
  "costos_fijos.ver": "Ver gastos fijos y recurrentes del negocio",
  "costos_fijos.crear": "Registrar gastos fijos o recurrentes",
  "costos_fijos.editar": "Editar gastos fijos",
  "costos_fijos.eliminar": "Eliminar gastos fijos",
  "costos_fijos.gestionar_recurrencia": "Cambiar el monto de un gasto desde ahora en adelante, omitir un mes o dejar de generar el gasto",
  // Pago IVA (pestaña "IVA" dentro de Egresos — sección propia, separada de
  // Costos Fijos y de Balance, para que sea fácil de encontrar al asignar permisos)
  "pago_iva.ver": "Ver la pestaña IVA en Egresos (débito, crédito, saldo acumulado y pagos registrados)",
  "pago_iva.crear": "Registrar un nuevo pago de IVA a la SET, desde la pestaña IVA de Egresos",
  "pago_iva.editar": "Editar un pago de IVA ya registrado",
  "pago_iva.eliminar": "Eliminar un pago de IVA ya registrado",
  // Configuración egresos
  "configuracion_egresos.ver_pago_contado": "Ver la configuración de pago de compras al contado (Configuración de Egresos)",
  "configuracion_egresos.gestionar_pago_contado": "Cambiar si las compras al contado se pagan automáticamente al registrarlas o desde Pagos Proveedores",
  "configuracion_egresos.gestionar_categorias": "Gestionar categorías de gastos — crear, editar y eliminar categorías disponibles para clasificar los gastos fijos (Pestaña Configuración de Egresos)",
  // Anticipos a proveedores
  "anticipos_proveedores.ver": "Ver el listado de anticipos registrados a proveedores y consultar su saldo disponible al registrar compras",
  "anticipos_proveedores.gestionar": "Registrar nuevos anticipos a proveedores y eliminar anticipos que aún no hayan sido aplicados a ninguna compra",
  // Proveedores
  "proveedores.ver": "Ver directorio de proveedores",
  "proveedores.crear": "Agregar nuevos proveedores",
  "proveedores.editar": "Editar datos de proveedores",
  "proveedores.eliminar": "Eliminar proveedores",
  // Pagos a proveedores
  "pagos_proveedores.ver": "Ver pagos registrados a proveedores a crédito",
  "pagos_proveedores.crear": "Registrar pagos a proveedores",
  "pagos_proveedores.editar": "Editar pagos a proveedores",
  "pagos_proveedores.eliminar": "Eliminar pagos a proveedores",
  // Catastro de Entidades
  "entidades.crear": "Crear nuevas entidades en el catastro",
  "entidades.editar": "Editar datos de entidades existentes",
  "entidades.eliminar": "Eliminar entidades del catastro",
  "entidades.ver_huella": "Ver la huella digital registrada de una entidad (dato biométrico sensible)",
  "entidades.cargar_huella": "Cargar, reemplazar o eliminar la huella digital de una entidad",
  "configuracion_catastro.gestionar_tipos": "Gestionar configuración del catastro (tipos de entidad, campos personalizados)",
  // Empleados
  "empleados.ver": "Ver nómina y datos de empleados",
  "empleados.crear": "Agregar nuevos empleados",
  "empleados.editar": "Editar datos de empleados en el catastro",
  "empleados.eliminar": "Eliminar empleados",
  "configuracion_catastro.gestionar_huella": "Elegir y configurar el lector de huella digital de la empresa (Catastro > Configuración)",
  "configuracion_catastro.gestionar_sueldos":"Gestionar el sueldo mínimo vigente desde la configuración del catastro",
  // Balance
  "balance.ver": "Ver balance, tesorería e IVA fiscal",
  "balance.editar": "Editar ajustes y pagos del balance",
  // Bancos
  "bancos.ver": "Ver cuentas bancarias y movimientos",
  "bancos.crear": "Agregar cuentas bancarias",
  "bancos.editar": "Editar cuentas bancarias",
  "bancos.eliminar": "Eliminar cuentas bancarias",
  "bancos.gestionar_procesadoras": "Gestionar procesadoras de cobro (Bancard, Dinelco, Pix…) y sus comisiones",
  // Plan de cuentas
  "plan_cuentas.ver": "Ver el plan de cuentas contables",
  "plan_cuentas.crear": "Crear cuentas en el plan de cuentas",
  "plan_cuentas.editar": "Editar cuentas del plan",
  "plan_cuentas.eliminar": "Eliminar cuentas del plan",
  "plan_cuentas.asignar_cuentas": "Asignar cuentas contables a clientes o módulos",
  "plan_cuentas.gestionar_cuotas": "Configurar usuarios permitidos y tope de cuotas en cuentas de tipo cuotas",
  "retenciones.ver_config": "Ver la tabla de retención de IVA a proveedores (Configuración global > Retenciones)",
  "retenciones.editar_config": "Editar sueldo mínimo, umbral y porcentajes de retención de IVA, y marcar empresas como agente de retención",
  "retenciones.omitir_retencion": "Tildar \"No aplicar retención\" al registrar un cobro de factura o un pago a proveedor cuando el importe se pagó completo sin descontar la retención (queda marcado en el pago)",
  "retenciones.gestionar_constancias": "Cargar constancias de exportador / no retención de proveedores en el Catastro",
  "plan_cuentas.traspasar_saldos": "Traspasar el saldo pendiente de una factura/compra a otra cuenta del plan, en Financiero > Traspaso de Saldos (incluye cruzar entre cobrar y pagar, ej. hacia/desde saldo a favor)",
  "programacion_caja.ver": "Ver la matriz de Programación de Caja (proyección de ingresos y egresos por vencimiento) en Financiero",
  "programacion_caja.excluir": "Marcar facturas de proveedores o sueldos como excluidos de un lote de pago (se corren al período siguiente sin afectar su vencimiento real)",
  "programacion_caja.generar_lote": "Generar la orden de pago (imprimir/exportar) de Programación de Caja — no ejecuta pagos ni asientos, solo marca los ítems como \"en proceso de pago\"",
  "transporte.ver": "Ver vehículos y Órdenes de Carga en el módulo Transporte (también habilita marcar entidades del Catastro como Chofer)",
  "transporte.crear": "Registrar vehículos y nuevas Órdenes de Carga",
  "transporte.editar": "Editar vehículos y Órdenes de Carga",
  "transporte.eliminar": "Eliminar vehículos y Órdenes de Carga",
  "transporte.cambiar_estado": "Cambiar el estado de una Orden de Carga (Pendiente / En Tránsito / Entregado / Facturado / Cancelado)",
  "transporte.imprimir_orden": "Imprimir la Orden de Carga para el viaje",
  "transporte.liquidar": "Liquidar el flete al chofer (egreso real contra una cuenta bancaria/caja, visible en Reportes Caja/Banco)",
  "restaurante.ver": "Ver tarjetas de comanda y el Salón en el módulo Restaurante",
  "restaurante.crear": "Agregar ítems de consumo (productos) a una comanda en el Salón",
  "restaurante.editar": "Quitar ítems de una comanda o renombrar la mesa/tarjeta",
  "restaurante.eliminar": "Eliminar una tarjeta de comanda (solo si está libre, sin consumo)",
  "restaurante.cerrar_cuenta": "Liberar una tarjeta de comanda tras cobrarla (desde el Salón, PDV o Facturas)",
  "restaurante.generar_tarjetas": "Generar tarjetas de comanda en lote o cargar una tarjeta física ya existente",
  "restaurante.liberar_sin_cobrar": "Liberar una mesa sin cobrar desde el Salón (se descarta el consumo y queda en Auditoría). Liberar tras un cobro sigue siendo restaurante.cerrar_cuenta",
  "restaurante.cocina": "Pantalla Cocina: ver los pedidos de todas las mesas y pasarlos a en proceso / terminado",
  "restaurante.disponibilidad": "Marcar platos como agotados o disponibles (el mozo no puede cargarlos si están agotados)",
  "restaurante.mozo": "Pantalla Mozos: ver pedidos de todas las mesas (incluye terminados) y marcarlos como entregados",
  "restaurante.configurar": "Elegir la categoría del menú y qué productos forman el menú de cocina",
  "recetario.ver": "Ver recetas agronómicas emitidas y el listado de ventas con productos controlados sin receta",
  "recetario.crear": "Emitir recetas agronómicas (también al vender productos que requieren receta en Facturas/PDV)",
  "recetario.editar": "Editar una receta emitida (ingeniero, chacra, superficie, dosis, observaciones)",
  "recetario.anular": "Anular una receta (queda registrada con el motivo, no se borra)",
  "recetario.imprimir": "Imprimir la Orden de aplicación de una receta",
  "recetario.gestionar_ingenieros": "Agregar, editar o desactivar los ingenieros agrónomos de la empresa",
  "recetario.gestionar_chacras": "Agregar, editar o desactivar las chacras de los clientes",
  "clinica_agenda.ver": "Ver la agenda de turnos de la clínica (por día/semana y por profesional)",
  "clinica_agenda.crear": "Agendar turnos (valida que el profesional no tenga otro turno en ese horario)",
  "clinica_agenda.editar": "Editar o reprogramar turnos y marcar confirmado/ausente (si no están liquidados ni facturados)",
  "clinica_agenda.cancelar": "Cancelar turnos",
  "clinica_agenda.atender": "Marcar un turno como atendido: fija el monto cobrado y calcula la comisión del profesional",
  "clinica_agenda.facturar": "Generar la factura/boleta del turno atendido y vincularla al turno",
  "clinica_agenda.asistencia": "Registrar la asistencia del paciente con la huella digital cargada en el Catastro (o manualmente)",
  "clinica_pacientes.ver": "Ver el listado y la ficha de pacientes (datos de contacto y fiscales vienen del Catastro)",
  "clinica_pacientes.crear": "Registrar pacientes (eligiendo o creando el cliente en el Catastro desde el mismo módulo)",
  "clinica_pacientes.editar": "Editar la ficha del paciente (seguro, afiliado, peso, antecedentes, estado actual)",
  "clinica_pacientes.eliminar": "Eliminar pacientes sin historial (con historial se inactivan)",
  "clinica_expediente.ver": "Ver el expediente clínico: indicaciones, evoluciones, estudios e historial de turnos. Datos sensibles",
  "clinica_expediente.escribir": "Cargar indicaciones (diagnóstico, ejercicios) y evoluciones del paciente",
  "clinica_expediente.archivos": "Subir estudios y archivos clínicos del paciente",
  "clinica_expediente.eliminar": "Eliminar indicaciones, evoluciones o estudios del expediente",
  "clinica_expediente.certificados": "Imprimir certificado fisioterapéutico e indicaciones/receta con la firma del profesional",
  "clinica_configuracion.ver": "Ver la configuración de la clínica (profesionales, servicios y comisiones)",
  "clinica_configuracion.profesionales": "Alta, edición y baja de profesionales",
  "clinica_configuracion.servicios": "Elegir qué productos del catálogo son servicios de la clínica y su duración",
  "clinica_configuracion.comisiones": "Definir la comisión de cada profesional (% de lo cobrado o monto fijo) y excepciones por servicio",
  "clinica_liquidaciones.ver": "Ver comisiones pendientes y liquidaciones emitidas",
  "clinica_liquidaciones.generar": "Armar la liquidación de comisiones de un profesional con sus turnos atendidos",
  "clinica_liquidaciones.pagar": "Registrar el pago de una liquidación (genera el egreso en la cuenta/caja elegida)",
  "clinica_liquidaciones.anular": "Anular una liquidación (libera los turnos; si estaba pagada, revierte el egreso)",
  "clinica_reportes.ver": "Ver el reporte de producción por profesional, servicio y seguro",
  "inventario_productos.marcar_receta": "Marcar un producto como \"requiere receta\" y cargar su ficha agronómica (ingrediente activo, toxicidad, carencia, dosis)",
  "importaciones.ver": "Ver carpetas de importación",
  "importaciones.crear": "Crear carpetas de importación",
  "importaciones.editar": "Editar carpetas abiertas y cambiar su estado",
  "importaciones.eliminar": "Eliminar o anular carpetas",
  "importaciones.liquidar": "Registrar el despacho aduanero y liquidar la carpeta (congela tasas y costeo)",
  "importaciones.nacionalizar": "Ingresar la mercadería importada al stock y costo promedio",
  "importaciones.reliquidar": "Reliquidar una carpeta ya liquidada (ajusta costos)",
  "importaciones.configurar": "Cargar aranceles (CSV) y configurar tasas aduaneras",
  "importaciones.ver_costos": "Ver costeo, impuestos y asiento de las importaciones",
  "bascula.ver": "Ver pesajes y tipos de descuento en el módulo Báscula",
  "bascula.crear": "Registrar nuevos pesajes de granos (compra o venta)",
  "bascula.editar": "Editar un pesaje que todavía no fue vinculado a Factura/Compra",
  "bascula.eliminar": "Eliminar un pesaje que todavía no fue vinculado a Factura/Compra",
  "bascula.configurar_descuentos": "Agregar, editar o desactivar los tipos de descuento de calidad de la empresa",
  "bascula.capturar_peso_equipo": "Registrar el peso bruto/tara leído directamente de la balanza (sin este permiso solo se puede digitar, si la balanza lo permite)",
  "equipos_bascula.ver": "Ver las balanzas configuradas en Configuración de Equipos / Báscula",
  "equipos_bascula.crear": "Dar de alta nuevas balanzas (puerto serial o TCP/IP)",
  "equipos_bascula.editar": "Modificar la configuración de una balanza",
  "equipos_bascula.eliminar": "Eliminar una balanza que no tenga pesajes registrados",
  "equipos_bascula.probar_captura": "Probar la lectura en vivo de una balanza desde su ficha",
  "equipos_bascula.gestionar_catalogos": "Agregar o quitar modelos de balanza del catálogo",
  "constructora_proyectos.ver": "Ver los proyectos de obra y su resumen en el módulo Constructora",
  "constructora_proyectos.crear": "Crear nuevos proyectos de obra (incluye crear el cliente en el Catastro desde el proyecto)",
  "constructora_proyectos.editar": "Editar los datos de un proyecto de obra",
  "constructora_proyectos.eliminar": "Eliminar un proyecto En estudio o Cancelado",
  "constructora_proyectos.cambiar_estado": "Cambiar el estado del proyecto (En estudio / Contratado / En ejecución / Pausado / Finalizado / Cancelado)",
  "constructora_proyectos.contrato_ver": "Ver y descargar los archivos del contrato del proyecto",
  "constructora_proyectos.contrato_cargar": "Cargar o eliminar archivos del contrato del proyecto",
  "constructora_proyectos.plan_cobros": "Definir y editar el plan de cobros (plazos de pago del cliente)",
  "constructora_presupuesto.ver": "Ver la planilla de cómputo métrico y presupuesto del proyecto",
  "constructora_presupuesto.editar": "Crear y editar rubros/ítems, avance por rubro y texto de facturación",
  "constructora_presupuesto.vincular_factura": "Facturar avances usando el texto y montos del presupuesto",
  "constructora_presupuesto.aprobar": "Aprobar, rechazar o reabrir presupuestos del proyecto (el aprobado rige facturación y reportes)",
  "constructora_presupuesto.copiar": "Copiar un presupuesto dentro del proyecto o desde otro proyecto de la empresa",
  "constructora_presupuesto.ver_costos": "Ver y editar la composición interna de los ítems (sub-ítems con costo y % de ganancia)",
  "constructora_cronograma.ver": "Ver el cronograma, atrasos y días de lluvia del proyecto",
  "constructora_cronograma.editar": "Crear/editar tareas, avance y fechas reales (sin mover fechas planificadas)",
  "constructora_cronograma.reprogramar": "Mover fechas planificadas de tareas ya cargadas (queda historial con motivo)",
  "constructora_cronograma.clima": "Registrar y borrar días de lluvia u otros eventos que atrasan la obra",
  "constructora_anticipos.ver": "Ver los anticipos recibidos del cliente en el proyecto",
  "constructora_anticipos.crear": "Registrar un anticipo de obra (ingresa a la cuenta y queda como crédito del cliente)",
  "constructora_anticipos.anular": "Anular un anticipo que todavía no se aplicó a ninguna factura",
  "constructora_compras.ver": "Ver las compras cargadas al proyecto (requiere además Compras → Ver)",
  "constructora_compras.crear": "Cargar compras al proyecto desde el módulo (requiere además Compras → Crear)",
  "constructora_gastos.ver": "Ver los gastos cargados al proyecto (requiere además Gastos → Ver)",
  "constructora_gastos.crear": "Cargar gastos al proyecto desde el módulo (requiere además Gastos → Crear)",
  "constructora_equipos.ver": "Ver los equipos del Inventario Técnico asignados a la obra y su uso en otros proyectos",
  "constructora_equipos.asignar": "Asignar un equipo/maquinaria a la obra por fechas (avisa si ya está en uso)",
  "constructora_equipos.editar": "Editar fechas y notas de una asignación de equipo",
  "constructora_equipos.eliminar": "Quitar un equipo de la obra",
  "constructora_personal.ver": "Ver el personal asignado a la obra y sus pagos",
  "constructora_personal.asignar": "Asignar empleados (existentes o nuevos) a la obra con fechas",
  "constructora_personal.editar": "Editar rol, fechas y tarifa del personal de obra",
  "constructora_personal.pagar": "Registrar pagos al personal de obra (egreso de cuenta) y emitir el recibo",
  "constructora_personal.eliminar": "Quitar personal de la obra (solo si no tiene pagos)",
  "constructora_facturacion.ver": "Ver las facturas emitidas al proyecto y su estado de cobro",
  "constructora_facturacion.facturar": "Emitir facturas/boletas del proyecto y aplicar anticipos (requiere además Facturas → Crear)",
  "constructora_reportes.ver": "Ver los reportes del proyecto: general, costos por grupo, cronograma y equipos",
  "constructora_configuracion.ver": "Ver la configuración del módulo Constructora (grupos de compra/gasto)",
  "constructora_configuracion.gestionar_grupos": "Agregar, editar y eliminar los grupos de compra/gasto de la empresa (no afecta a otras empresas)",
  "constructora_configuracion.gestionar_unidades": "Agregar, editar y eliminar las unidades de medida (m2, m3, kg…) que se ofrecen en los presupuestos de obra de la empresa",
  // Reportes
  "reportes.ver": "Acceder al panel de reportes",
  "reportes.exportar": "Exportar reportes en PDF o Excel",
  "reportes.balance": "Ver reporte de balance (mensual, anual o detallado)",
  "reportes.flow_facturaciones": "Ver Flow de Facturaciones — facturas, notas de crédito y compras",
  "reportes.cliente_detallado": "Ver reporte detallado por cliente",
  "reportes.libro_ventas": "Ver el Libro Ventas (RUC, cliente, fecha, número de factura, total, contado/crédito, IVA y notas) y exportarlo a Excel",
  "reportes.comisiones_vendedores": "Ver el reporte de Comisiones de Vendedores (ventas, notas de crédito, base comisionable y liquidación neta por vendedor en el período)",
  "reportes.costo_hectarea": "Ver el reporte de Costo por Hectárea (costo de los insumos aplicados según las recetas agronómicas, por chacra, dividido por su superficie)",
  "reportes.retenciones": "Ver el reporte de Retenciones de IVA (practicadas a proveedores en compras y sufridas de clientes en ventas, por pago)",
  "reportes.gastos_categoria": "Ver el reporte de Gastos por Categoría (detalle de gastos pagados agrupados por categoría, con filtro por categoría y fecha)",
  // Vendedores (registro de vendedores y % de comisión — asignación opcional en Facturas)
  "vendedores.ver": "Ver el listado de vendedores y su % de comisión",
  "vendedores.crear": "Registrar nuevos vendedores",
  "vendedores.editar": "Editar datos o % de comisión de un vendedor",
  "vendedores.eliminar": "Desactivar un vendedor",
  "vendedores.ver_comisiones": "Ver en Egresos → Comisiones las comisiones generadas por ventas cobradas (solo lectura)",
  "vendedores.cargar_comprobante_comision": "Registrar el comprobante / factura del comisionado en cada comisión generada (Egresos > Comisiones), antes o al momento del pago",
  "vendedores.pagar_comisiones": "Pagar comisiones de ventas desde Egresos (selecciona una o todas, adjunta la factura del comisionado y debita una cuenta)",
  "vendedores.anular_pago_comision": "Anular un pago de comisiones ya registrado (las comisiones vuelven a quedar pendientes)",
  "vendedores.configurar_comisiones": "Configurar cómo se comisiona (por venta o por producto), reglas por usuario y generar comisiones de ventas ya cobradas",
  "reportes.flow_financieros": "Ver Flow de Financieros — ingresos varios, recibos y gastos fijos",
  "reportes.presupuestos": "Ver reporte de presupuestos",
  "reportes.proveedores": "Ver reporte de proveedores y deudas",
  "reportes.catastros": "Ver reporte unificado de Clientes, Proveedores y Empleados (ID, documento, teléfono, correo, estado)",
  "reportes.balancete_obra": "Ver el reporte Balancete - Obra: ganancia/pérdida por obra cruzando cobros reales, costo de materiales y de personal",
  "reportes.balance_gerencial": "Ver el Balance Analítico Gerencial Completo (activo, pasivo, patrimonio y cuadro de mando)",
  "reportes.garantias": "Ver el reporte de Garantías: pagarés recibidos de clientes y concedidos a proveedores, vigentes, cobrados y vencidos",
  "reportes.prestamos": "Ver reporte detallado de préstamos con alias, referencia, cuenta, saldo, cuotas vencidas y plan de cuentas",
  "reportes.iva": "Ver reporte de IVA fiscal (crédito y débito)",
  "reportes.caja_banco": "Ver reporte de movimientos de caja y banco",
  "reportes.movimientos": "Ver el reporte Movimientos: extracto de una cuenta tipo banco, con saldo corriente por fila y filtros por tipo (crédito, débito, transferencias)",
  "reportes.productos_stock": "Ver reporte de productos y stock disponible",
  "reportes.stock_historial": "Ver historial de entradas y salidas de stock",
  "reportes.kardex": "Ver ficha de mercadería con movimientos y saldo por producto",
  "reportes.cuentas_cobrar": "Ver reporte de cuentas por cobrar (facturas pendientes de cobro por cliente)",
  "reportes.cuentas_pagar": "Ver reporte de cuentas por pagar (compras pendientes de pago por proveedor)",
  "reportes.estado_cuenta": "Ver estado de cuenta de un cliente o proveedor con vencimientos y saldo deudor/acreedor",
  "reportes.filtrar_tipo_factura": "Filtrar el reporte de cuentas por cobrar por tipo de factura (requiere módulo Tipos de factura activo).",
  "reportes.inventario_tecnico": "Ver reporte de activos del inventario técnico",
  // Inventario técnico
  "inventario.ver": "Ver activos del inventario técnico de clientes",
  "inventario.crear": "Agregar activos al inventario técnico",
  "inventario.editar": "Editar activos del inventario",
  "inventario.eliminar": "Eliminar activos del inventario",
  "configuracion_inventario.ver": "Ver la sección de Configuración del Inventario Técnico",
  "configuracion_inventario.gestionar_unicidad": "Activar o desactivar si se permiten nombres de activo duplicados dentro de una misma empresa",
  "credenciales.ver": "Ver credenciales de acceso asociadas a clientes (AnyDesk, SSH, etc.)",
  "credenciales.editar": "Editar credenciales de acceso",
  "alertas.ver": "Ver alertas de vencimiento y mantenimiento",
  "alertas.crear": "Crear alertas de vencimiento",
  "alertas.editar": "Editar alertas",
  "alertas.eliminar": "Eliminar alertas",
  // Productos y stock
  "inventario_productos.ver": "Ver catálogo de productos",
  "inventario_productos.crear": "Agregar productos al catálogo",
  "inventario_productos.editar": "Editar datos de productos",
  "inventario_productos.eliminar": "Eliminar productos del catálogo",
  "inventario_productos.crear_servicio": "Crear productos del tipo servicio (sin stock físico)",
  "inventario_productos.stock_inicial": "Definir el stock inicial al crear un producto",
  "inventario_productos.ajustar_stock": "Realizar ajustes manuales de stock",
  "inventario_productos.transferir_stock": "Transferir mercadería entre empresas del grupo (sucursales)",
  "inventario_productos.ver_depositos": "Ver los depósitos, el stock por depósito y las transferencias internas",
  "inventario_productos.gestionar_depositos": "Configurar depósitos (modo, predeterminados, alta/baja de depósitos y ubicaciones)",
  "inventario_productos.transferir_depositos": "Transferir mercadería entre depósitos de la misma empresa (con remito)",
  "inventario_productos.anular_transferencia_deposito": "Anular una transferencia entre depósitos",
  "inventario_productos.ver_stock_madre": "Consultar (solo lectura) el stock de la empresa madre del grupo",
  "inventario_productos.ver_stock_sucursales": "Consultar (solo lectura) el stock de las sucursales del grupo",
  "inventario_productos.ver_costo_compra": "Ver el costo de compra (precio_costo) de los productos",
  "inventario_productos.editar_costo": "Modificar a mano el costo de un producto ya creado (el costo normalmente viene de las compras). Al crear un producto nuevo el costo se carga libremente una sola vez. Requiere también ver el costo de compra.",
  "inventario_productos.gestionar_descuentos": "Editar los campos descuento máximo (%) y fecha de vencimiento en cada producto — sin este permiso los campos de descuento del formulario están ocultos",
  "inventario_productos.generar_etiquetas": "Acceso al generador de códigos de barra — ver, generar y asignar códigos a productos, e imprimir etiquetas escaneables",
  "inventario_productos.generar_codigo_barras": "Generar y asignar códigos de barras internos según la configuración activa",
  "inventario_productos.editar_codigo_barras": "Modificar solamente el código de barras de un producto",
  "inventario_productos.eliminar_codigo_barras": "Quitar solamente el código de barras de un producto",
  "configuracion_productos.configurar_codigos_barras": "Configurar tipo, prefijo, quiet zone y recomendación de códigos de barras",
  "configuracion_productos.ver_disenos_etiqueta": "Ver la lista de diseños de etiqueta de la empresa y elegir cuál está activo",
  "configuracion_productos.crear_disenos_etiqueta": "Crear nuevos diseños de etiqueta (medidas, columnas, separaciones y qué mostrar)",
  "configuracion_productos.editar_disenos_etiqueta": "Editar las medidas y el contenido de un diseño de etiqueta existente",
  "configuracion_productos.eliminar_disenos_etiqueta": "Eliminar un diseño de etiqueta (no se puede eliminar el último de la empresa)",
  "historial_costos.ver": "Ver el historial de cambios de costo de cada producto — registra cada variación de precio_costo con fecha, motivo (compra o ajuste manual) y usuario responsable",
  "historial_stock.ver": "Ver historial completo de entradas y salidas de stock por producto",
  "unidades_medida.ver": "Ver el catálogo de unidades de medida",
  "unidades_medida.crear": "Agregar nuevas unidades de medida al catálogo",
  "unidades_medida.editar": "Editar unidades de medida existentes",
  "unidades_medida.eliminar": "Eliminar unidades de medida del catálogo",
  "configuracion_productos.ver": "Ver la configuración de categorías, subcategorías y generador de SKU",
  "configuracion_productos.deshabilitar_categoria": "Deshabilitar / habilitar categorías globales de productos para la empresa (no las elimina: quedan apagadas y dejan de aparecer en los selectores)",
  "configuracion_productos.hacer_global_categoria": "Convertir una categoría de productos de la empresa en GLOBAL — pasa a existir en TODAS las empresas (cada una podrá deshabilitarla, no eliminarla)",
  "configuracion_productos.gestionar_categorias": "Crear, editar y eliminar categorías de productos — incluyendo sus campos de trazabilidad (código de fabricante, lote, fecha de vencimiento)",
  "configuracion_productos.gestionar_subcategorias": "Crear, editar y eliminar subcategorías de productos — agrupaciones dentro de cada categoría (ej: Mercaderías › Electrónica)",
  "configuracion_productos.gestionar_atributos": "Crear, editar y eliminar atributos controlados de productos — como Marca, Talle, Color o Presentación",
  "configuracion_productos.habilitar_clasificaciones": "Activar o desactivar el uso de subcategorías, atributos y variantes por empresa — sin este permiso el usuario puede gestionar registros pero no apagar el uso global",
  "configuracion_productos.gestionar_unicidad": "Activar o desactivar si una empresa puede repetir nombres de productos. El SKU siempre permanece único.",
  "configuracion_productos.codigo_automatico": "Gestionar el modo de generación automática de códigos SKU — definir prefijo, separador y longitud de secuencia por subcategoría",
  // Sueldos
  "sueldos.ver": "Ver la pestaña de sueldos, empleados, adelantos y extras dentro de Egresos",
  "sueldos.pagar": "Registrar pagos de sueldo seleccionando cuenta bancaria obligatoria",
  "sueldos.gestionar_adelantos": "Registrar y eliminar adelantos de sueldo",
  "sueldos.gestionar_extras": "Registrar y eliminar extras o bonificaciones de sueldo",
  "sueldos.deshacer_pago": "Deshacer un pago de sueldo registrado",
  "sueldos.ajustar_sueldo": "Actualizar sueldo base e historial salarial desde el módulo de sueldos",
  "sueldos.liquidar": "Liquidar o desvincular empleados para que no vuelvan a generar sueldo pendiente",
  // Administración
  "usuarios.ver": "Ver listado de usuarios del sistema",
  "usuarios.crear": "Crear nuevos usuarios",
  "usuarios.editar": "Editar usuarios y sus permisos",
  "usuarios.eliminar": "Eliminar usuarios",
  "configuracion_global.ver": "Ver la ventana de Configuración global del sistema (ajustes que afectan a todas las empresas). Sin este permiso la sección no aparece",
  "configuracion_global.editar": "Modificar los ajustes de la Configuración global (requiere también el permiso de ver)",
  "auditoria.ver": "Ver el registro de auditoría: quién hizo qué y cuándo",
  "auditoria.ver_info_registro": "Ver el ícono de info en cada registro para saber quién lo creó, cuándo, y quién lo modificó por última vez",
  "auditoria.forzar_logout": "Cerrar forzosamente la sesión activa de otros usuarios restringidos desde el panel de Auditoría",
  // Soporte
  "soporte.ver": "Ver y crear los propios tickets de soporte",
  "soporte.crear": "Crear tickets de soporte",
  "soporte.ver_todos": "Ver todos los tickets del sistema, no solo los propios",
  "soporte.responder": "Responder tickets y ver el historial interno completo",
  "soporte.gestionar": "Cambiar estado, prioridad y asignación de cualquier ticket",
  "soporte.eliminar": "Eliminar tickets de soporte",
  // Avisos (crear/editar/eliminar es exclusivo de admin/gerente por rol, sin permiso granular)
  "avisos.ver": "Ver los avisos publicados para la empresa del usuario",
  // Financiero — Transferencias
  "transferencias.ver": "Ver la página de Financiero y las tarjetas de cuentas bancarias",
  "transferencias.crear": "Registrar nuevas transferencias entre cuentas",
  "transferencias.editar": "Editar la descripción y fecha de transferencias registradas",
  "transferencias.eliminar": "Eliminar (anular) transferencias existentes",
  "transferencias.historial": "Ver el historial completo de transferencias con filtros",
  "transferencias.editar_tc": "Modificar manualmente el tipo de cambio al crear una transferencia entre cuentas de distinta moneda (sin este permiso, se usa el TC de cotizaciones y no se puede editar)",
  "configuracion_financiero.gestionar_acceso_transferencias": "Configurar qué usuarios pueden ver cada cuenta bancaria en transferencias y sus umbrales de saldo — Pestaña Configuración del módulo Financiero",
  "configuracion_financiero.gestionar_acceso_bancos": "Configurar qué cuentas bancarias puede ver cada usuario en el sistema y en reportes — Pestaña Configuración del módulo Financiero",
  "configuracion_financiero.gestionar_fechas_movimientos": "Configurar si se pueden registrar movimientos (facturas, compras, pagos, cheques, etc.) con fecha pasada o futura, en general o por usuario — Pestaña Configuración del módulo Financiero",
  "cotizaciones.ver": "Ver la pestaña de Cotizaciones en Financiero: tipos de cambio USD y BRL del día e historial",
  "cotizaciones.gestionar": "Sincronizar tipos de cambio desde DNIT y cargar cotizaciones manualmente",
  "cotizaciones.editar_en_ventas": "Modificar el tipo de cambio manualmente en facturas de venta y cobros (ingresos) — por defecto se toma de Cotizaciones",
  "cotizaciones.editar_en_compras": "Modificar el tipo de cambio manualmente en compras, notas de compra y pagos a proveedores (egresos) — útil cuando la moneda del pago difiere de la factura",
  // Configuraciones de Ventas
  "configuracion_ventas.gestionar_descuentos": "Configurar qué descuentos puede aplicar cada usuario en facturas, boletas y presupuestos",
  "configuracion_ventas.gestionar_recibos": "Configurar numeración y modo de emisión de recibos de pago",
  "configuracion_ventas.gestionar_timbrado": "Gestionar timbrado fiscal, puntos de expedición y numeración manual o automática",
  "configuracion_ventas.ver_huecos_timbrado": "Ver, dentro de Configuración de Timbrado, los números de factura liberados por una eliminación (huecos en la numeración) y crear una factura reutilizando ese número puntual — no habilita el resto de la gestión de timbrado",
  "configuracion_ventas.gestionar_delivery": "Elegir los productos del catálogo que representan el delivery (ej: delivery sin costo, delivery de ₲ 15.000), el formato de la orden (A4 horizontal doble vía A5, A4 vertical o ticket 80 mm) y si la orden se imprime al emitir la factura o se emite aparte",
  "facturas.imprimir_delivery": "Al facturar un producto de delivery, cargar repartidor, dirección y referencia de entrega, e imprimir la Orden de Delivery con los datos de la empresa emisora y del cliente del Catastro",
  "configuracion_ventas.gestionar_cobro_contado": "Definir si las ventas al contado se cobran automáticamente al registrarlas (por empresa) o se cobran después con el botón Pagar",
  "configuracion_ventas.gestionar_impresion": "Definir si al crear una factura/boleta el botón guarda solamente o guarda e imprime automáticamente (por empresa), y qué modelos de impresión (A4, doble vía, compacta, ticket) están habilitados",
  "pdv.ver": "Acceder a la pantalla de Punto de Venta rápido",
  "pdv.operar": "Escanear productos y crear facturas reales desde el PDV",
  "pdv.configurar": "Definir cliente estándar, cuenta de caja y punto de expedición por usuario",
  "reportes.descuentos": "Ver el reporte de productos con descuentos habilitados (máximo %, vencimiento y estado) en Reportes › Inventario",
  "reportes.mercaderias_pendientes": "Ver el reporte de mercadería facturada pendiente de entrega y comprada pendiente de recibir",
  "entregas.ver": "Ver la sección Entregas pendientes (Ventas) y Mercaderías por recibir (Egresos)",
  "entregas.crear": "Escanear/cargar cantidades y confirmar entregas a clientes y recepciones de proveedores (mueve stock)",
  "entregas.anular": "Anular una entrega o recepción ya confirmada (devuelve el stock al estado anterior)",
  "entregas.configurar": "Definir si 'Entrega posterior' viene marcada por defecto en Facturas y Compras",
  "reportes.vencimiento_productos": "Ver el reporte de vencimiento de productos (SKU, cód. fabricante, lote, fecha de vencimiento) en Reportes › Inventario",
  // Lectura de Bombas
  "lectura_bombas.ver": "Acceder al módulo de Lectura de Bombas y ver historial de turnos",
  "lectura_bombas.apertura_turno": "Registrar apertura de turno: declara lecturas iniciales por pico",
  "lectura_bombas.cierre_turno": "Registrar cierre de turno: declara lecturas finales y calcula total expedido",
  "lectura_bombas.configurar": "Configurar bombas (asignación pico-combustible) y catálogo de combustibles con precios",
  "lectura_bombas.reportes": "Generar y exportar reporte de volumetría de bombas (PDF incluido)",
  "lectura_bombas.eliminar": "Eliminar registros de expedición del historial de turnos",
  // Producción
  "produccion.ver": "Acceder al módulo de Producción y ver el panel KDS (Kanban de cocina)",
  "produccion_kds.ver": "Ver el panel KDS y las órdenes activas de cocina",
  "produccion_kds.tomar_orden": "Registrar nuevas órdenes/comandas desde el PDV",
  "produccion_kds.avanzar": "Avanzar el estado de las órdenes en el Kanban",
  "produccion_kds.eliminar": "Eliminar comandas cargadas por error",
  "produccion_kds.imprimir": "Imprimir ticket de cocina para una orden",
  "produccion_kds.etapa_a_recibir": "Ver solo la columna \"A recibir\" del Kanban y avanzar los pedidos que están ahí (no ve las demás etapas, salvo que tenga también otro permiso de etapa)",
  "produccion_kds.etapa_en_preparacion": "Ver solo la columna \"En preparación\" del Kanban y avanzar los pedidos que están ahí",
  "produccion_kds.etapa_listo": "Ver solo la columna \"Listo\" del Kanban y avanzar los pedidos que están ahí",
  "produccion_kds.etapa_despachado": "Ver solo la columna \"Despachado\" del Kanban",
  "produccion_facturacion.boleta": "Emitir boleta al facturar una comanda",
  "produccion_facturacion.factura_legal": "Emitir factura legal con timbrado al facturar una comanda",
  "produccion_historial.ver": "Ver historial de órdenes, totales y estados",
  "produccion_fichas.ver": "Ver fichas técnicas de producción",
  "produccion_fichas.crear": "Crear nuevas fichas técnicas por producto",
  "produccion_fichas.editar": "Editar ingredientes, costos y modo de una ficha técnica",
  "produccion_fichas.eliminar": "Eliminar fichas técnicas",
  "produccion_fichas.producir_stock": "Producir stock terminado consumiendo ingredientes de la ficha",
  "produccion_configuracion.ver": "Acceder a la configuración de producción",
  "produccion_configuracion.catalogos": "Permiso legacy: gestiona categorías, variantes y adicionales de producción",
  "produccion_configuracion.categorias": "Crear, editar y eliminar categorías propias de producción",
  "produccion_configuracion.variantes": "Crear, editar y eliminar variantes de producción, como sabores u opciones",
  "produccion_configuracion.adicionales": "Crear, editar y eliminar adicionales de producción con precio",
  "produccion_configuracion.delivery": "Gestionar zonas y costos de delivery",
  "produccion_configuracion.modos": "Gestionar modo default de producción y ticket de cocina",
  // Hotelería
  "hoteleria.ver_panel": "Ver el panel de habitaciones con estado en tiempo real (disponible, ocupado, reservado)",
  "hoteleria.checkin": "Realizar el check-in de una reserva pendiente para activar la estadía",
  "hoteleria.checkout": "Procesar el check-out, cerrar la estadía y generar la factura prefillada para confirmar y guardar",
  "hoteleria.reserva": "Crear nuevas reservas de habitaciones para huéspedes con fecha de entrada programada",
  "hoteleria.editar_reserva": "Editar reservas pendientes: modificar fechas, habitaciones, huéspedes y datos de la estadía",
  "hoteleria.cancelar_reserva": "Cancelar reservas pendientes antes de que se realice el check-in",
  "hoteleria.registrar_gasto": "Registrar consumos durante la estadía (frigobar, restaurant, lavandería, etc.) y eliminar gastos por error",
  "hoteleria.ver_extracto": "Ver el estado de cuenta completo de la estadía: hospedaje + consumos + total a cobrar",
  "hoteleria.crear_hab": "Crear nuevas habitaciones con número, tipo, precios y horarios de check-in/out",
  "hoteleria.editar_hab": "Editar los datos de habitaciones existentes (tipo, precios, capacidad, horarios)",
  "hoteleria.eliminar_hab": "Eliminar habitaciones que no tengan reservas activas",
  "hoteleria.reportes": "Acceder al histórico completo de reservas con búsqueda por huésped, RUC, habitación y filtros de período",
  "hoteleria.registrar_anticipo": "Registrar cobros adelantados en reservas activas (pendientes o en curso), vinculados a una cuenta bancaria",
  "hoteleria.ver_anticipos": "Ver el listado de anticipos hoteleros registrados — pendientes y aplicados a facturas",
  "hoteleria.ver_huespedes": "Acceder al historial de huéspedes frecuentes registrados en el sistema hotelero",
  "hoteleria.editar_huespedes": "Editar datos de huéspedes frecuentes (nombre, documento, nacionalidad, motivo habitual)",
  "hoteleria.eliminar_huespedes": "Eliminar registros de huéspedes frecuentes del historial",
  // BackUp
  "backup.db_exportar": "Permite descargar un archivo .arbackup encriptado con todos los datos de la empresa. Solo puede restaurarse dentro de este sistema.",
  "backup.db_restaurar": "Permite restaurar datos desde un archivo .arbackup. ADVERTENCIA: reemplaza todos los datos actuales de la empresa. Asignar solo a personal de confianza.",
  // Cheques
  "cheques.ver": "Acceder al módulo de Emisión de Cheques y ver talonarios y hojas",
  "cheques.registrar_chequera": "Crear nuevos talonarios de cheques y generar sus hojas en bulk",
  "cheques.editar_chequera": "Editar datos de un talonario existente (banco, serie, fecha de activación)",
  "cheques.emitir": "Emitir un cheque: completar beneficiario, valor y fechas en una hoja disponible",
  "cheques.descontar": "Marcar un cheque diferido como descontado eligiendo la fecha real de cobro — esa fecha es la que aparece en el reporte Caja/Banco",
  "cheques.liberar": "Liberar un cheque emitido o descontado para volver a usarlo (desvincula el pago). Requisito para poder anular un pago de gasto/compra/proveedor que se pagó con ese cheque",
  "cheques.anular": "Anular un cheque emitido o diferido ingresando un motivo",
  "cheques.eliminar_chequera": "Eliminar un talonario completo — solo si todas sus hojas están en estado Disponible",
  "cheques.configurar_impresion": "Configurar las medidas (mm) de impresión de cheques, en general o por cuenta bancaria específica",
  // Garantías (pagarés)
  "garantias.ver": "Acceder al módulo de Garantías y ver los pagarés recibidos de clientes y concedidos a proveedores",
  "garantias.emitir": "Editar los datos del documento físico de una garantía (número de pagaré, tipo de documento del firmante)",
  "garantias.cobrar": "Marcar una garantía como cobrada/pagada, generando el recibo correspondiente",
  "garantias.anular": "Anular, cancelar o marcar como destruido el documento físico de una garantía",
  // Licencias
  "licencias.autoextender": "Si la licencia de la empresa está vencida, permite sumar 2 días de gracia desde la pantalla de bloqueo — una única vez por vencimiento. Se vuelve a habilitar automáticamente si el administrador del sistema extiende la licencia más adelante. Asignar solo a una persona de confianza en la empresa.",
  // Préstamos
  "prestamos.ver": "Acceder al módulo de Préstamos y ver préstamos bancarios, a empleados y a proveedores",
  "prestamos.crear_empleado": "Crear préstamos otorgados a empleados",
  "prestamos.crear_proveedor": "Crear préstamos otorgados a proveedores",
  "prestamos.crear_bancario": "Crear préstamos bancarios recibidos por la empresa",
  "prestamos.gestionar": "Permiso legacy: acceso completo para crear préstamos de cualquier tipo",
  "prestamos.pagar": "Registrar el pago manual de cuotas de préstamos",
  "prestamos.cuota_manual": "Definir un plan manual con monto distinto para cada cuota al crear un préstamo",
  "prestamos.descuento_empleado_interes": "Exonerar interés normal por pago anticipado en préstamos a empleados",
  "prestamos.descuento_empleado_extra": "Aplicar descuentos a empleados mayores al interés exonerable",
  "prestamos.descuento_proveedor": "Registrar descuentos por pronto pago en préstamos a proveedores",
  "prestamos.descuento_bancario": "Registrar descuentos por pronto pago en préstamos bancarios",
  "prestamos.cancelar": "Cancelar préstamos activos",
  // Loteadora
  "loteadora.ver": "Acceder al módulo de Loteadora: ver panel con mapa, lotes, ventas y reservas",
  "loteadora.gestionar_lotes": "Crear, editar y eliminar loteamientos y lotes con datos catastrales",
  "loteadora.reservar": "Registrar y liberar reservas de lotes con seña",
  "loteadora.gestionar_ventas": "Registrar ventas al contado o en cuotas y cancelar ventas activas",
  "loteadora.cobrar_cuotas": "Cobrar cuotas de ventas: emite factura y confirma el pago de la cuota",
  "loteadora.ver_catastro": "Acceder a la pestaña de Catastro con datos SNC (CCC, finca madre, linderos, planilla UTM)",
  "loteadora.configurar": "Acceder a la pestaña de Configuración del módulo Loteadora: definir el punto de inicio del mapa global (latitud, longitud y zoom)",
};

function ChipSearch({ chips, setChips, inputVal, setInputVal, placeholder }) {
  const addChip = () => {
    const term = inputVal.trim().replace(/,$/, "");
    if (term && !chips.includes(term)) setChips(prev => [...prev, term]);
    setInputVal("");
  };
  return (
    <div className="space-y-2">
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {chips.map((chip, idx) => (
            <span key={chip} className="inline-flex items-center gap-1.5 bg-arandu-blue/15 border border-arandu-blue/30 text-arandu-blue rounded-full px-2.5 py-1 text-xs">
              <Search className="w-3 h-3" />
              {chip}
              <button type="button" onClick={() => setChips(prev => prev.filter((_, i) => i !== idx))} className="hover:text-white"><X className="w-3 h-3" /></button>
            </span>
          ))}
          <button type="button" onClick={() => setChips([])} className="text-xs text-slate-500 hover:text-slate-300 px-2 py-1 rounded-full border border-white/10">Limpiar</button>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => {
            if ((e.key === "Enter" || e.key === ",") && inputVal.trim()) { e.preventDefault(); addChip(); }
            if (e.key === "Backspace" && !inputVal && chips.length) setChips(prev => prev.slice(0, -1));
          }}
          placeholder={placeholder}
          className="w-full bg-arandu-dark-light border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-white text-sm focus:outline-none focus:border-arandu-blue/60"
        />
      </div>
    </div>
  );
}

function matchChips(chips, inputVal, texto) {
  const terms = [...chips, ...(inputVal.trim() ? [inputVal.trim()] : [])];
  if (!terms.length) return true;
  const haystack = texto.toLowerCase();
  return terms.every(term => haystack.includes(term.toLowerCase()));
}

const UsuariosPage = () => {
  const { token, user: currentUser, login, activeEmpresaPropia } = useContext(AuthContext);
  // Empresa activa en pantalla al momento de la acción — se manda solo como referencia
  // para el log de Auditoría (el usuario en sí no pertenece a una única empresa).
  const logoTipoCtx = activeEmpresaPropia?.slug || "";
  const [usuarios, setUsuarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresasPropias, setEmpresasPropias] = useState([]);
  const [permisosDisponibles, setPermisosDisponibles] = useState({});
  const [permSearch, setPermSearch] = useState("");
  const [copiarDeId, setCopiarDeId] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    password: "",
    role: "usuario",
    permisos: [],
    empresas_asignadas: [],
    empresas_todos_clientes: true,
    logos_asignados: [],
    modulo_unico: "",
  });
  const [expandedUser, setExpandedUser] = useState(null);
  const [searchChips, setSearchChips] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  // Dashboard config (solo para edición)
  const [dashboardConfig, setDashboardConfig] = useState({ scope: "todo", usuarios_ids: [] });
  const [usuariosDisponiblesDash, setUsuariosDisponiblesDash] = useState([]);
  const [loadingDashUsuarios, setLoadingDashUsuarios] = useState(false);

  // Módulos config — scope de movimientos por grupo de permisos (solo para edición)
  const [modulosConfig, setModulosConfig] = useState({});
  const [modulosConfigChanged, setModulosConfigChanged] = useState(false);

  const getModulosPermitidosPorLogos = (logos) => {
    const ids = (logos || []).map(String);
    if (!ids.length) return [];
    const seleccionadas = empresasPropias.filter(ep => ids.includes(String(ep.id)));
    // Si los logos asignados no coinciden con ninguna empresa propia cargada (aún no se cargaron
    // o los IDs son de otra colección), asumir todos los módulos habilitados para no bloquear permisos.
    if (!seleccionadas.length) return DEFAULT_EMPRESA_MODULOS;
    return [...new Set(seleccionadas.flatMap(ep => modulosHabilitadosEmpresa(ep) || DEFAULT_EMPRESA_MODULOS))];
  };

  const modulosPermitidosForm = getModulosPermitidosPorLogos(formData.logos_asignados);
  const permisoPermitidoPorEmpresa = (permiso) => {
    const permisoModulo = String(permiso || "").split(".")[0];
    const moduloEmpresa = PERMISO_A_MODULO_EMPRESA[permisoModulo];
    if (!moduloEmpresa || !modulosPermitidosForm.includes(moduloEmpresa)) return false;
    return currentUser?.role === "admin" || currentUser?.role === "gerente" || (currentUser?.permisos || []).includes(permiso);
  };

  // Copiar permisos de otro usuario (modo "reemplazar" o "sumar")
  const copiarPermisosDe = (modo) => {
    const origen = usuarios.find(u => String(u.id) === String(copiarDeId));
    if (!origen) { toast.error("Elegí un usuario de origen"); return; }
    setFormData(prev => {
      const logos = (prev.logos_asignados || []).length > 0
        ? prev.logos_asignados
        : (origen.logos_asignados || []).map(String);
      const permisosOrigen = origen.permisos || [];
      const next = {
        ...prev,
        logos_asignados: logos,
        permisos: modo === "sumar" ? [...new Set([...(prev.permisos || []), ...permisosOrigen])] : [...permisosOrigen],
        ...(modo === "reemplazar" ? {
          empresas_todos_clientes: origen.empresas_todos_clientes !== false,
          empresas_asignadas: origen.empresas_asignadas || [],
        } : {}),
      };
      const limpio = limpiarPermisosFueraDeModulos(next);
      const omitidos = permisosOrigen.length - limpio.permisos.filter(x => permisosOrigen.includes(x)).length;
      setTimeout(() => toast.success(`Permisos copiados de ${origen.name || origen.email}` + (omitidos > 0 ? ` (${omitidos} omitidos: módulo no habilitado o sin autorización tuya)` : "")), 0);
      return limpio;
    });
  };

  const limpiarPermisosFueraDeModulos = (next) => ({
    ...next,
    permisos: (next.permisos || []).filter(permiso => {
      const permisoModulo = String(permiso || "").split(".")[0];
      const moduloEmpresa = PERMISO_A_MODULO_EMPRESA[permisoModulo];
      const habilitado = !!moduloEmpresa && getModulosPermitidosPorLogos(next.logos_asignados).includes(moduloEmpresa);
      return habilitado && (currentUser?.role === "admin" || currentUser?.role === "gerente" || (currentUser?.permisos || []).includes(permiso));
    }),
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [usersRes, empresasRes, permisosRes, propRes] = await Promise.all([
        fetch(`${API}/admin/usuarios`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/entidades`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/permisos-disponibles`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API}/admin/empresas-propias`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
      ]);

      if (usersRes.ok) setUsuarios(await usersRes.json());
      if (empresasRes.ok) setEmpresas(await empresasRes.json());
      if (permisosRes.ok) setPermisosDisponibles(await permisosRes.json());
      if (propRes.ok) setEmpresasPropias(await propRes.json());
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCopiarDeId("");
    setFormData({
      email: "",
      name: "",
      password: "",
      role: "usuario",
      permisos: [],
      empresas_asignadas: [],
      empresas_todos_clientes: true,
      logos_asignados: [],
      modulo_unico: "",
    });
    setEditingId(null);
    setShowForm(false);
    setModulosConfigChanged(false);
  };

  // ── Buscador de permisos (por módulo, nombre, descripción o clave) ──
  const _norm = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const permTerms = _norm(permSearch).split(/\s+/).filter(Boolean);
  const permVisible = (modulo, accion, grupoLabel) => {
    if (permTerms.length === 0) return true;
    const perm = `${modulo}.${accion}`;
    const texto = _norm([grupoLabel, MODULOS_LABELS[modulo], ACCIONES_LABELS[accion], PERMISOS_NOMBRES[perm], PERMISO_DESC[perm], perm].join(" "));
    return permTerms.every(t => texto.includes(t));
  };

  const togglePermiso = (modulo, accion) => {
    const perm = `${modulo}.${accion}`;
    if (!permisoPermitidoPorEmpresa(perm)) return;
    setFormData(prev => ({
      ...prev,
      permisos: prev.permisos.includes(perm)
        ? prev.permisos.filter(p => p !== perm)
        : [...prev.permisos, perm]
    }));
  };

  const toggleAllModule = (modulo) => {
    const acciones = permisosDisponibles[modulo] || [];
    const modulePerms = acciones.map(a => `${modulo}.${a}`);
    if (!modulePerms.some(permisoPermitidoPorEmpresa)) return;
    const allSelected = modulePerms.every(p => formData.permisos.includes(p));

    setFormData(prev => ({
      ...prev,
      permisos: allSelected
        ? prev.permisos.filter(p => !modulePerms.includes(p))
        : [...new Set([...prev.permisos, ...modulePerms])]
    }));
  };

  const toggleAllGroup = (subModulos) => {
    const allGroupPerms = subModulos.flatMap(m =>
      (permisosDisponibles[m] || []).map(a => `${m}.${a}`)
    ).filter(permisoPermitidoPorEmpresa);
    if (!allGroupPerms.length) return;
    const allSelected = allGroupPerms.every(p => formData.permisos.includes(p));
    setFormData(prev => ({
      ...prev,
      permisos: allSelected
        ? prev.permisos.filter(p => !allGroupPerms.includes(p))
        : [...new Set([...prev.permisos, ...allGroupPerms])]
    }));
  };

  const toggleEmpresa = (empresaId) => {
    setFormData(prev => {
      const empresasActuales = (prev.empresas_asignadas || []).map(String);
      const id = String(empresaId);
      const exists = empresasActuales.includes(id);

      return {
        ...prev,
        empresas_todos_clientes: false,
        empresas_asignadas: exists
          ? empresasActuales.filter(empId => empId !== id)
          : [...empresasActuales, id]
      };
    });
  };

  // Calcula los slugs de las empresas propias seleccionadas actualmente
  const getSlugsSeleccionados = (logos_asignados) => {
    const ids = (logos_asignados || []).map(String);
    return empresasPropias
      .filter(ep => ids.includes(String(ep.id)))
      .map(ep => ep.slug);
  };

  // Empresas (clientes) que pertenecen a las logos seleccionadas
  const getEmpresasFiltradas = (logos_asignados) => {
    const slugs = getSlugsSeleccionados(logos_asignados);
    if (!slugs.length) return [];
    return empresas.filter(e => slugs.includes(e.logo_tipo));
  };

  const selectAllEmpresas = () => {
    setFormData(prev => ({
      ...prev,
      empresas_todos_clientes: !prev.empresas_todos_clientes,
      empresas_asignadas: [],
    }));
  };

  const toggleLogo = (logoId) => {
    setFormData(prev => {
      const current = (prev.logos_asignados || []).map(String);
      const id = String(logoId);
      const isAdding = !current.includes(id);
      const newLogos = isAdding ? [...current, id] : current.filter(x => x !== id);

      // Gestión automática de clientes según empresa propia
      const empresaPropia = empresasPropias.find(ep => String(ep.id) === id);
      let newEmpresas = (prev.empresas_asignadas || []).map(String);

      if (empresaPropia) {
        const clientesDeEsta = empresas
          .filter(e => e.logo_tipo === empresaPropia.slug)
          .map(e => String(e.id));

        if (isAdding) {
          // Al agregar empresa propia → auto-seleccionar todos sus clientes
          newEmpresas = [...new Set([...newEmpresas, ...clientesDeEsta])];
        } else {
          // Al quitar empresa propia → remover sus clientes de la selección
          newEmpresas = newEmpresas.filter(empId => !clientesDeEsta.includes(empId));
        }
      }

      return limpiarPermisosFueraDeModulos({
        ...prev,
        logos_asignados: newLogos,
        empresas_asignadas: newEmpresas,
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.name || (!editingId && !formData.password)) {
      toast.error("Complete todos los campos requeridos");
      return;
    }

    if (!editingId && ["usuario", "gerente"].includes(formData.role) && (formData.logos_asignados || []).length === 0) {
      toast.error("Debe asignar al menos una empresa antes de crear el usuario");
      return;
    }

    try {
      const payload = {
        ...limpiarPermisosFueraDeModulos(formData),
        empresas_asignadas: formData.empresas_todos_clientes ? [] : (formData.empresas_asignadas || []).map(String),
        empresas_todos_clientes: !!formData.empresas_todos_clientes,
        logos_asignados: (formData.logos_asignados || []).map(String),
        modulo_unico: formData.role === "usuario" ? (formData.modulo_unico || "") : "",
        logo_tipo_ctx: logoTipoCtx,
      };

      const url = editingId
        ? `${API}/admin/usuarios/${editingId}`
        : `${API}/admin/usuarios`;

      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        // Guardar dashboard_config si estamos editando
        if (editingId) {
          try {
            await fetch(`${API}/admin/usuarios/${editingId}/dashboard-config`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ ...dashboardConfig, logo_tipo_ctx: logoTipoCtx }),
            });
          } catch { /* best-effort */ }
          try {
            const mcRes = await fetch(`${API}/admin/usuarios/${editingId}/modulos-config`, {
              method: "PUT",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ modulos_config: modulosConfig, logo_tipo_ctx: logoTipoCtx }),
            });
            if (!mcRes.ok) {
              const mcErr = await mcRes.json().catch(() => ({}));
              toast.error(mcErr.detail || "Error al guardar movimientos por módulo");
            }
          } catch {
            toast.error("Error de conexión al guardar movimientos por módulo");
          }
        }
        toast.success(editingId ? "Usuario actualizado" : "Usuario creado");
        resetForm();
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al guardar");
      }
    } catch (err) {
      toast.error("Error de conexion");
    }
  };

  const handleEdit = (usuario) => {
    setEditingId(usuario.id);
    setFormData({
      email: usuario.email,
      name: usuario.name,
      password: "",
      role: usuario.role,
      permisos: usuario.permisos || [],
      empresas_asignadas: (usuario.empresas_asignadas || []).map(String),
      empresas_todos_clientes: usuario.empresas_todos_clientes ?? ((usuario.empresas_asignadas || []).length === 0),
      logos_asignados: (usuario.logos_asignados || []).map(String),
      modulo_unico: usuario.modulo_unico || "",
    });
    const cfg = usuario.dashboard_config || {};
    setDashboardConfig({ scope: cfg.scope || "todo", usuarios_ids: cfg.usuarios_ids || [] });
    // Cargar modulos_config directamente desde el objeto de la lista (siempre fresco tras fetchAll)
    setModulosConfig(usuario.modulos_config || {});
    setModulosConfigChanged(false);
    setShowForm(true);
    fetchUsuariosDisponiblesDash(usuario.id);
  };

  const fetchUsuariosDisponiblesDash = async (userId) => {
    setLoadingDashUsuarios(true);
    try {
      const res = await fetch(`${API}/admin/usuarios/${userId}/dashboard-config/usuarios-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setUsuariosDisponiblesDash(await res.json());
    } catch { }
    finally { setLoadingDashUsuarios(false); }
  };

  const handleToggleActivo = async (usuario) => {
    const accion = usuario.activo === false ? "activar" : "inactivar";
    if (!window.confirm(`¿${accion.charAt(0).toUpperCase() + accion.slice(1)} a ${usuario.nombre || usuario.email}?`)) return;
    try {
      const res = await fetch(`${API}/admin/usuarios/${usuario.id}/toggle-activo${logoTipoCtx ? `?logo_tipo=${encodeURIComponent(logoTipoCtx)}` : ""}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Usuario ${data.activo ? "activado" : "inactivado"}`);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al cambiar estado");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const ingresarComoUsuario = async (usuario) => {
    if (!canImpersonateUser(usuario)) return;
    if (!window.confirm(`Vas a cerrar tu sesión actual e ingresar como ${usuario.name || usuario.email}. ¿Continuar?`)) return;
    try {
      const res = await fetch(`${API}/admin/usuarios/${usuario.id}/impersonar${logoTipoCtx ? `?logo_tipo=${encodeURIComponent(logoTipoCtx)}` : ""}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        await login(data.user, data.access_token);
        toast.success(`Ingresaste como ${data.user?.name || data.user?.email}`);
        window.location.href = "/sistema";
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "No se pudo ingresar como este usuario");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Seguro que desea eliminar este usuario?")) return;

    try {
      const res = await fetch(`${API}/admin/usuarios/${id}${logoTipoCtx ? `?logo_tipo=${encodeURIComponent(logoTipoCtx)}` : ""}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success("Usuario eliminado");
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al eliminar");
      }
    } catch (err) {
      toast.error("Error de conexion");
    }
  };

  const getPermisosSummary = (permisos) => {
    if (!permisos || !permisos.length) return "Sin permisos";
    const modulos = [...new Set(permisos.map(p => p.split(".")[0]))];
    return modulos.map(m => MODULOS_LABELS[m] || m).join(", ");
  };

  const getEmpresasSummary = (usuario) => {
    if (usuario?.empresas_todos_clientes) return "Todos los clientes";
    const asignadas = usuario?.empresas_asignadas || [];
    if (!asignadas.length) return "Sin clientes asignados";
    const found = empresas.filter(e => asignadas.map(String).includes(String(e.id)));
    if (!found.length) return `${asignadas.length} clientes`;
    return found.map(e => e.nombre).join(", ");
  };

  const getLogosSummary = (logos) => {
    if (!logos || !logos.length) return "Sin empresas asignadas";
    const found = empresasPropias.filter(e => logos.map(String).includes(String(e.id)));
    if (!found.length) return `${logos.length} empresa(s)`;
    return found.map(e => e.nombre).join(", ");
  };


  const ROLE_RANK = { usuario: 1, gerente: 2, admin: 3, super_admin: 4 };
  const rankOf = (role) => ROLE_RANK[role] || 1;
  const canManageUser = (targetUsuario) => {
    if (!currentUser) return false;
    if (targetUsuario.id === currentUser.id) return false; // no editarse a sí mismo
    return rankOf(currentUser.role) > rankOf(targetUsuario.role);
  };
  const canImpersonateUser = (targetUsuario) => {
    if (!currentUser || !targetUsuario) return false;
    if (!["admin", "super_admin"].includes(currentUser.role)) return false;
    if (targetUsuario.id === currentUser.id) return false;
    return rankOf(currentUser.role) > rankOf(targetUsuario.role);
  };
  const editingUsuario = usuarios.find(u => u.id === editingId);
  const filteredUsuarios = usuarios.filter(usuario => {
    const texto = [
      usuario.name, usuario.email, usuario.role,
      getLogosSummary(usuario.logos_asignados),
      getEmpresasSummary(usuario),
      getPermisosSummary(usuario.permisos),
      ...(usuario.permisos || [])
    ].filter(Boolean).join(" ");
    return matchChips(searchChips, searchInput, texto);
  });

  return (
    <div className="min-h-screen bg-arandu-dark p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/sistema" className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-2">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-arandu-blue" />
                <h1 className="font-heading text-3xl font-bold text-white">Usuarios</h1>
              </div>
              <p className="text-slate-400 text-sm mt-1">Gestión de usuarios y permisos</p>
            </div>
          </div>

          <Button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="bg-arandu-red hover:bg-arandu-red-dark text-white"
            data-testid="new-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Usuario
          </Button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-2xl p-6 my-8"
              >
                <h2 className="font-heading text-xl font-bold text-white mb-6">
                  {editingId ? "Editar Usuario" : "Nuevo Usuario"}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">Nombre *</label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="Nombre completo"
                        data-testid="user-name-input"
                      />
                    </div>

                    <div>
                      <label className="text-slate-400 text-sm mb-1 block">
                        Usuario / Email *
                        <span className="text-slate-600 font-normal ml-1 text-xs">(nombre de usuario o correo completo)</span>
                      </label>
                      <Input
                        type="text"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value.trim() })}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="usuario o correo@dominio.com"
                        autoComplete="off"
                        data-testid="user-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-slate-400 text-sm mb-1 block">
                      Contraseña {editingId ? "(dejar vacío para no cambiar)" : "*"}
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="bg-arandu-dark border-white/10 text-white"
                        placeholder="********"
                        data-testid="user-password-input"
                      />
                      {editingId && canImpersonateUser(editingUsuario) && (
                        <Button
                          type="button"
                          onClick={() => ingresarComoUsuario(editingUsuario)}
                          className="shrink-0 bg-amber-500/15 border border-amber-400/30 text-amber-200 hover:bg-amber-500/25"
                          title="Ingresar como este usuario para probar permisos"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Ingresar
                        </Button>
                      )}
                    </div>
                    {editingId && (
                      <p className="text-xs text-slate-500 mt-1">
                        Por seguridad no se muestra la contraseña actual. Super admin puede ingresar como este usuario sin cambiar su clave.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-slate-400 text-sm mb-2 block">Rol *</label>
                    <div className="flex gap-3 flex-wrap">
                      {currentUser?.role === "admin" && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, role: "admin" })}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                            formData.role === "admin"
                              ? "border-arandu-red bg-arandu-red/10 text-arandu-red"
                              : "border-white/10 bg-arandu-dark text-slate-400 hover:border-white/20"
                          }`}
                          data-testid="role-superadmin-btn"
                        >
                          <Shield className="w-4 h-4" />
                          Super Admin
                        </button>
                      )}

                      {currentUser?.role === "admin" && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, role: "gerente" })}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                            formData.role === "gerente"
                              ? "border-amber-500 bg-amber-500/10 text-amber-400"
                              : "border-white/10 bg-arandu-dark text-slate-400 hover:border-white/20"
                          }`}
                          data-testid="role-admin-btn"
                        >
                          <UserCog className="w-4 h-4" />
                          Administrador
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, role: "usuario" })}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                          formData.role === "usuario"
                            ? "border-arandu-blue bg-arandu-blue/10 text-arandu-blue"
                            : "border-white/10 bg-arandu-dark text-slate-400 hover:border-white/20"
                        }`}
                        data-testid="role-usuario-btn"
                      >
                        <Lock className="w-4 h-4" />
                        Usuario Restringido
                      </button>
                    </div>
                  </div>

                  {(formData.role === "usuario" || formData.role === "gerente") && (
                    <div className="space-y-4 pt-2">

                      {/* ── NUESTRAS EMPRESAS (logos_asignados) ── */}
                      {empresasPropias.length > 0 && (
                        <div className={`border rounded-lg overflow-hidden ${
                          !editingId && (formData.logos_asignados || []).length === 0
                            ? "border-red-500/40"
                            : "border-white/10"
                        }`}>
                          <div className="bg-arandu-dark p-3 flex items-center gap-2">
                            <Star className="w-4 h-4 text-amber-400" />
                            <span className="text-white font-medium text-sm">Nuestras Empresas</span>
                            <span className="text-slate-500 text-xs">
                              ({(formData.logos_asignados || []).length}/{empresasPropias.length})
                            </span>
                            {!editingId && (formData.logos_asignados || []).length === 0 && (
                              <span className="ml-auto text-red-400 text-xs">Requerido</span>
                            )}
                          </div>

                          <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                            {empresasPropias.map(ep => {
                              const isSelected = (formData.logos_asignados || [])
                                .map(String)
                                .includes(String(ep.id));

                              return (
                                <label
                                  key={ep.id}
                                  onClick={() => toggleLogo(String(ep.id))}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                    isSelected
                                      ? "border-amber-500/50 bg-amber-500/10 text-amber-300"
                                      : "border-white/5 bg-arandu-dark text-slate-400 hover:border-white/10"
                                  }`}
                                >
                                  <div
                                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                      isSelected
                                        ? "bg-amber-500 border-amber-500"
                                        : "border-white/20"
                                    }`}
                                  >
                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                  </div>

                                  {ep.logo ? (
                                    <img src={ep.logo} alt={ep.nombre} className="w-5 h-5 object-contain rounded" />
                                  ) : (
                                    <div
                                      className="w-5 h-5 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                                      style={{ backgroundColor: ep.color || "#64748b" }}
                                    >
                                      {ep.nombre.charAt(0)}
                                    </div>
                                  )}

                                  <span className="truncate">{ep.nombre}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* ── MÓDULO ÚNICO — solo para usuario ── */}
                      {formData.role === "usuario" && (
                        <div className="border border-white/10 rounded-lg p-3 bg-arandu-dark">
                          <label className="text-white font-medium text-sm block mb-1">Acceso a un solo módulo (opcional)</label>
                          <p className="text-slate-500 text-xs mb-2">
                            Si elegís un módulo, este usuario entra directo ahí y no puede navegar a ningún otro lado (solo cerrar sesión),
                            aunque tenga más permisos. Útil para cocina, mozos o un puesto fijo.
                          </p>
                          <select
                            value={formData.modulo_unico || ""}
                            onChange={(e) => setFormData({ ...formData, modulo_unico: e.target.value })}
                            className="w-full bg-arandu-dark-light border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                          >
                            <option value="">— Acceso normal (todos sus módulos) —</option>
                            {MODULOS_UNICOS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                          </select>
                        </div>
                      )}

                      {/* ── PERMISOS — solo para usuario ── */}
                      {formData.role === "usuario" && <div className="border border-white/10 rounded-lg overflow-hidden">
                        <div className="bg-arandu-dark p-3 flex items-center gap-2">
                          <Shield className="w-4 h-4 text-arandu-blue" />
                          <span className="text-white font-medium text-sm">Permisos por Módulo</span>
                          <div className="relative ml-auto w-full max-w-xs">
                            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                              type="text"
                              value={permSearch}
                              onChange={e => setPermSearch(e.target.value)}
                              placeholder="Buscar permiso (ej: anular, cheques, costo)…"
                              className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-7 py-1.5 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-arandu-blue/50"
                              data-testid="perm-search"
                            />
                            {permSearch && (
                              <button type="button" onClick={() => setPermSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        {!editingId && (() => {
                          const mias = (formData.logos_asignados || []).map(String);
                          const candidatos = usuarios.filter(u => u.role === "usuario" && (
                            mias.length === 0 || (u.logos_asignados || []).map(String).some(l => mias.includes(l))
                          ));
                          return (
                            <div className="px-3 py-2 border-t border-white/5 bg-white/[0.02] flex flex-wrap items-center gap-2" data-testid="copiar-permisos">
                              <span className="text-slate-400 text-xs">Copiar permisos de:</span>
                              <select value={copiarDeId} onChange={e => setCopiarDeId(e.target.value)}
                                style={{ colorScheme: "dark" }}
                                className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-2 py-1.5 text-xs min-w-[220px]">
                                <option value="">Elegí un usuario…</option>
                                {candidatos.map(u => (
                                  <option key={u.id} value={u.id}>{u.name || u.email} ({(u.permisos || []).length} permisos)</option>
                                ))}
                              </select>
                              <button type="button" disabled={!copiarDeId} onClick={() => copiarPermisosDe("reemplazar")}
                                className="text-xs px-3 py-1.5 rounded-lg bg-arandu-blue/20 border border-arandu-blue/40 text-arandu-blue disabled:opacity-40 hover:bg-arandu-blue/30">
                                Reemplazar
                              </button>
                              <button type="button" disabled={!copiarDeId} onClick={() => copiarPermisosDe("sumar")}
                                className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-slate-300 disabled:opacity-40 hover:bg-white/10">
                                Sumar
                              </button>
                              <span className="text-slate-600 text-[11px]">
                                {mias.length === 0 ? "Si no elegiste empresa, se toman las del usuario origen." : "Solo usuarios de las empresas elegidas."}
                              </span>
                            </div>
                          );
                        })()}

                        <div className="p-3 space-y-3">
                          {/* Clientes dentro de permisos */}
                          {(() => {
                            const empresasFiltradas = getEmpresasFiltradas(formData.logos_asignados);
                            const selectedIds = (formData.empresas_asignadas || []).map(String);
                            const noLogosSelected = (formData.logos_asignados || []).length === 0;
                            const todos = !!formData.empresas_todos_clientes;
                            return (
                              <div className="border border-purple-500/20 rounded-lg overflow-hidden bg-purple-500/5">
                                <div className="bg-arandu-dark/80 p-3 flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-purple-400" />
                                    <span className="text-white font-medium text-sm">Entidades</span>
                                  </div>
                                  <label
                                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-all ${
                                      todos ? "border-purple-500 bg-purple-500/20 text-purple-200" : "border-white/10 text-slate-400"
                                    }`}
                                    onClick={(e) => { e.preventDefault(); selectAllEmpresas(); }}
                                  >
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${todos ? "bg-purple-500 border-purple-500" : "border-white/20"}`}>
                                      {todos && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    Todos (incluye nuevos)
                                  </label>
                                </div>
                                {!todos && (
                                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                                    {noLogosSelected ? (
                                      <p className="text-slate-500 text-sm col-span-2">Seleccioná primero una empresa propia.</p>
                                    ) : empresasFiltradas.length === 0 ? (
                                      <p className="text-slate-500 text-sm col-span-2">Sin entidades en esta empresa.</p>
                                    ) : empresasFiltradas.map(emp => {
                                      const isSelected = selectedIds.includes(String(emp.id));
                                      return (
                                        <label key={emp.id} onClick={() => toggleEmpresa(String(emp.id))}
                                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${
                                            isSelected ? "border-purple-500/50 bg-purple-500/10 text-purple-300" : "border-white/5 text-slate-400"
                                          }`}>
                                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? "bg-purple-500 border-purple-500" : "border-white/20"}`}>
                                            {isSelected && <Check className="w-3 h-3 text-white" />}
                                          </div>
                                          <span className="truncate">{emp.nombre}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                                {todos && (
                                  <p className="px-3 pb-3 text-slate-500 text-xs">Verá todas las entidades del catastro (clientes, proveedores y empleados), incluso las que se creen después.</p>
                                )}
                              </div>
                            );
                          })()}
                          {Object.entries(EMPRESA_MODULOS)
                            .filter(([moduloEmpresa, config]) => {
                              if (!modulosPermitidosForm.includes(moduloEmpresa)) return false;
                              // Debe tener al menos un sub-módulo con permisos disponibles
                              if (permTerms.length > 0) {
                                return (config.permisos || []).some(m => (permisosDisponibles[m] || []).some(a => permVisible(m, a, config.label)));
                              }
                              return (config.permisos || []).some(m => !!permisosDisponibles[m]);
                            })
                            .map(([moduloEmpresa, config]) => {
                              const subModulos = (config.permisos || []).filter(m => !!permisosDisponibles[m]);
                              const allGroupPerms = subModulos.flatMap(m =>
                                (permisosDisponibles[m] || []).map(a => `${m}.${a}`)
                              );
                              const selectedGroupCount = allGroupPerms.filter(p => formData.permisos.includes(p)).length;
                              const allGroupSelected = allGroupPerms.length > 0 && selectedGroupCount === allGroupPerms.length;
                              const someGroupSelected = selectedGroupCount > 0 && !allGroupSelected;

                              return (
                                <div key={moduloEmpresa} className="border border-white/10 rounded-lg overflow-hidden">
                                  {/* Cabecera del grupo */}
                                  <div
                                    className="flex items-center justify-between px-3 py-2.5 bg-arandu-dark/80 cursor-pointer hover:bg-white/5 transition-all"
                                    onClick={() => toggleAllGroup(subModulos)}
                                    title={config.desc || ""}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                        allGroupSelected
                                          ? "bg-arandu-blue border-arandu-blue"
                                          : someGroupSelected
                                          ? "bg-arandu-blue/40 border-arandu-blue/60"
                                          : "border-white/20"
                                      }`}>
                                        {(allGroupSelected || someGroupSelected) && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      <span className="text-white text-sm font-semibold">{config.label}</span>
                                    </div>
                                    <span className="text-slate-500 text-xs">{selectedGroupCount}/{allGroupPerms.length}</span>
                                  </div>

                                  {/* Sub-módulos dentro del grupo */}
                                  <div className="divide-y divide-white/5">
                                    {subModulos.filter(m => permTerms.length === 0 || (permisosDisponibles[m] || []).some(a => permVisible(m, a, config.label))).map(modulo => {
                                      const acciones = permisosDisponibles[modulo] || [];
                                      const accionesVis = acciones.filter(a => permVisible(modulo, a, config.label));
                                      const modulePerms = acciones.map(a => `${modulo}.${a}`);
                                      const selectedCount = modulePerms.filter(p => formData.permisos.includes(p)).length;
                                      const allSelected = selectedCount === modulePerms.length;
                                      const someSelected = selectedCount > 0 && !allSelected;
                                      // Ocultar subMódulo header si es el único en el grupo
                                      const showSubHeader = subModulos.length > 1;

                                      return (
                                        <div key={modulo} className="px-3 py-2 bg-arandu-dark/30">
                                          {showSubHeader && (
                                            <div
                                              className="flex items-center gap-2 mb-1.5 cursor-pointer group w-fit"
                                              onClick={() => toggleAllModule(modulo)}
                                            >
                                              <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                                                allSelected
                                                  ? "bg-arandu-blue/80 border-arandu-blue/80"
                                                  : someSelected
                                                  ? "bg-arandu-blue/30 border-arandu-blue/50"
                                                  : "border-white/20"
                                              }`}>
                                                {(allSelected || someSelected) && <Check className="w-2.5 h-2.5 text-white" />}
                                              </div>
                                              <span className="text-slate-400 text-xs font-medium group-hover:text-slate-200 transition-colors">
                                                {MODULOS_LABELS[modulo] || modulo}
                                              </span>
                                              <span className="text-slate-600 text-xs">{selectedCount}/{acciones.length}</span>
                                            </div>
                                          )}
                                          {PERM_SUBGROUPS[modulo] ? (
                                            <div className="space-y-2.5">
                                              {PERM_SUBGROUPS[modulo].filter(group => group.acciones.some(a => accionesVis.includes(a))).map(group => (
                                                <div key={group.label}>
                                                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                                    {group.label}
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {group.acciones.filter(a => accionesVis.includes(a)).map(accion => {
                                                      const perm = `${modulo}.${accion}`;
                                                      const active = formData.permisos.includes(perm);
                                                      const desc = PERMISO_DESC[perm] || `${MODULOS_LABELS[modulo] || modulo}: ${ACCIONES_LABELS[accion] || accion}`;
                                                      return (
                                                        <button
                                                          key={perm}
                                                          type="button"
                                                          title={desc}
                                                          onClick={() => togglePermiso(modulo, accion)}
                                                          className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                                            active
                                                              ? "bg-arandu-blue/20 border-arandu-blue/50 text-arandu-blue"
                                                              : "border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-300"
                                                          }`}
                                                          data-testid={`perm-${perm}`}
                                                        >
                                                          {PERMISOS_NOMBRES[perm] || ACCIONES_LABELS[accion] || accion}
                                                        </button>
                                                      );
                                                    })}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="flex flex-wrap gap-1.5">
                                              {accionesVis.map(accion => {
                                                const perm = `${modulo}.${accion}`;
                                                const active = formData.permisos.includes(perm);
                                                const desc = PERMISO_DESC[perm] || `${MODULOS_LABELS[modulo] || modulo}: ${ACCIONES_LABELS[accion] || accion}`;
                                                return (
                                                  <button
                                                    key={perm}
                                                    type="button"
                                                    title={desc}
                                                    onClick={() => togglePermiso(modulo, accion)}
                                                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                                                      active
                                                        ? "bg-arandu-blue/20 border-arandu-blue/50 text-arandu-blue"
                                                        : "border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-300"
                                                    }`}
                                                    data-testid={`perm-${perm}`}
                                                  >
                                                    {PERMISOS_NOMBRES[perm] || ACCIONES_LABELS[accion] || accion}
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })
                          }
                          {modulosPermitidosForm.length === 0 && (
                            <div className="text-slate-500 text-sm bg-arandu-dark rounded-lg border border-white/5 px-3 py-4">
                              Seleccioná al menos una empresa propia para habilitar permisos.
                            </div>
                          )}
                        </div>
                      </div>}
                    </div>
                  )}

                  {formData.role === "usuario" && (formData.permisos.includes("reportes.caja_banco") || formData.permisos.includes("reportes.movimientos")) && (
                    <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-3">
                      <p className="text-cyan-300/90 text-xs">
                        Este usuario puede usar los reportes <strong className="text-cyan-200">Caja/Banco</strong> y/o <strong className="text-cyan-200">Movimientos</strong> según qué permiso tenga tildado — ambos comparten la misma configuración de cuentas habilitadas. Quién asigna cuentas necesita <strong className="text-cyan-200">Bancos → Asignar acceso reporte</strong> en Bancos → ícono usuarios en cada cuenta.
                      </p>
                    </div>
                  )}

                  {formData.role === "admin" && (
                    <div className="bg-arandu-red/10 border border-arandu-red/20 rounded-lg p-3">
                      <p className="text-arandu-red text-sm flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Super Admin: acceso total a todas las empresas y funciones.
                      </p>
                    </div>
                  )}
                  {formData.role === "gerente" && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                      <p className="text-amber-400 text-sm flex items-center gap-2">
                        <UserCog className="w-4 h-4" />
                        Administrador de empresa: puede gestionar usuarios de sus empresas asignadas.
                      </p>
                    </div>
                  )}

                  {/* ── Configuración de Movimientos por Módulo (solo al editar) ── */}
                  {editingId && (
                    <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/3">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="w-4 h-4 text-orange-400" />
                        <p className="text-slate-300 text-sm font-semibold">Movimiento por Módulo</p>
                      </div>
                      <p className="text-slate-500 text-xs">Define qué registros puede ver este usuario en cada módulo transaccional.</p>

                      <div className="space-y-3">
                        {[
                          { key: "ventas_base", label: "Ventas / Facturas / Recibos / Notas de crédito" },
                          { key: "presupuestos", label: "Presupuestos" },
                          { key: "ingresos_varios", label: "Ingresos sin factura" },
                          { key: "egresos_base", label: "Egresos / Compras" },
                        ].map(({ key, label }) => {
                          const grupoScope = (modulosConfig[key] || {}).scope || "todo";
                          const grupoUsuariosIds = (modulosConfig[key] || {}).usuarios_ids || [];
                          const setGrupoScope = (s) => {
                            setModulosConfigChanged(true);
                            setModulosConfig(prev => ({
                              ...prev,
                              [key]: { ...((prev[key]) || {}), scope: s, usuarios_ids: s !== "personalizado" ? [] : (prev[key]?.usuarios_ids || []) }
                            }));
                          };
                          const toggleGrupoUsuario = (uid) => {
                            setModulosConfigChanged(true);
                            setModulosConfig(prev => {
                              const ids = (prev[key]?.usuarios_ids || []);
                              return { ...prev, [key]: { scope: "personalizado", usuarios_ids: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] } };
                            });
                          };

                          return (
                            <div key={key} className="border border-white/10 rounded-lg overflow-hidden">
                              <div className="flex items-center justify-between px-3 py-2 bg-arandu-dark/60">
                                <span className="text-slate-300 text-xs font-medium">{label}</span>
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                                  grupoScope === "usuario" ? "border-orange-500/40 bg-orange-500/10 text-orange-300" :
                                  grupoScope === "personalizado" ? "border-purple-500/40 bg-purple-500/10 text-purple-300" :
                                  "border-white/15 bg-white/5 text-slate-400"
                                }`}>
                                  {grupoScope === "usuario" ? "Solo el suyo" : grupoScope === "personalizado" ? "Personalizado" : "Todo"}
                                </span>
                              </div>
                              <div className="px-3 py-2 bg-orange-500/3 border-t border-orange-500/10">
                                <div className="flex gap-1.5">
                                  {[
                                    { value: "usuario", label: "Solo el suyo" },
                                    { value: "todo", label: "Todo" },
                                    { value: "personalizado", label: "Personalizado" },
                                  ].map(opt => (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      onClick={() => setGrupoScope(opt.value)}
                                      className={`flex-1 py-1.5 px-1.5 rounded text-[10px] font-medium border transition-all ${
                                        grupoScope === opt.value
                                          ? "border-orange-500/60 bg-orange-500/15 text-orange-300"
                                          : "border-white/10 text-slate-500 hover:border-white/20 hover:text-slate-400"
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                                {grupoScope === "personalizado" && (
                                  <div className="mt-2 space-y-1 max-h-28 overflow-y-auto pr-1">
                                    {loadingDashUsuarios ? (
                                      <div className="text-slate-500 text-[10px] animate-pulse">Cargando...</div>
                                    ) : usuariosDisponiblesDash.length === 0 ? (
                                      <div className="text-slate-500 text-[10px] italic">No hay otros usuarios en esta empresa.</div>
                                    ) : usuariosDisponiblesDash.map(u => {
                                      const isSel = grupoUsuariosIds.includes(u.id);
                                      return (
                                        <button
                                          key={u.id}
                                          type="button"
                                          onClick={() => toggleGrupoUsuario(u.id)}
                                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-[10px] transition-all ${
                                            isSel
                                              ? "bg-orange-500/15 border border-orange-500/40 text-orange-300"
                                              : "bg-white/5 border border-white/10 text-slate-400 hover:bg-white/8"
                                          }`}
                                        >
                                          <div className={`w-3.5 h-3.5 rounded flex items-center justify-center border flex-shrink-0 ${isSel ? "bg-orange-500 border-orange-400" : "border-white/20"}`}>
                                            {isSel && <Check className="w-2.5 h-2.5 text-white" />}
                                          </div>
                                          <span className="font-medium truncate">{u.name || u.email}</span>
                                          <span className="text-slate-500 ml-auto">{u.role}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Vista del Panel Principal (solo al editar) ── */}
                  {editingId && (
                    <div className="border border-white/10 rounded-xl p-4 space-y-3 bg-white/3">
                      <div className="flex items-center gap-2 mb-1">
                        <BarChart2 className="w-4 h-4 text-cyan-400" />
                        <p className="text-slate-300 text-sm font-semibold">Vista del Panel Principal</p>
                      </div>
                      <p className="text-slate-500 text-xs">Define qué transacciones ve este usuario en sus estadísticas del panel.</p>

                      {/* Selector de scope */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: "usuario", label: "Solo el suyo", icon: <User2 className="w-4 h-4" />, desc: "Solo sus propias transacciones" },
                          { value: "todo", label: "Todo", icon: <Eye className="w-4 h-4" />, desc: "Todas las transacciones de la empresa" },
                          { value: "personalizado", label: "Personalizado", icon: <Users className="w-4 h-4" />, desc: "Elegir usuarios específicos" },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setDashboardConfig(prev => ({ ...prev, scope: opt.value }))}
                            className={`flex flex-col items-center gap-1 p-3 rounded-lg border text-xs transition-all ${
                              dashboardConfig.scope === opt.value
                                ? "border-cyan-500/60 bg-cyan-500/10 text-cyan-300"
                                : "border-white/10 text-slate-400 hover:border-white/20 hover:text-slate-300"
                            }`}
                          >
                            {opt.icon}
                            <span className="font-semibold">{opt.label}</span>
                            <span className="text-[10px] text-center opacity-70">{opt.desc}</span>
                          </button>
                        ))}
                      </div>

                      {/* Lista de usuarios cuando scope = personalizado */}
                      {dashboardConfig.scope === "personalizado" && (
                        <div className="mt-2 space-y-2">
                          <p className="text-slate-400 text-xs">Seleccioná los usuarios cuyas transacciones quiere ver (siempre incluye las propias):</p>
                          {loadingDashUsuarios ? (
                            <div className="text-slate-500 text-xs animate-pulse">Cargando usuarios...</div>
                          ) : usuariosDisponiblesDash.length === 0 ? (
                            <div className="text-slate-500 text-xs italic">No hay otros usuarios en esta empresa.</div>
                          ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                              {usuariosDisponiblesDash.map(u => {
                                const isSelected = (dashboardConfig.usuarios_ids || []).includes(u.id);
                                return (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => setDashboardConfig(prev => {
                                      const ids = prev.usuarios_ids || [];
                                      return {
                                        ...prev,
                                        usuarios_ids: isSelected
                                          ? ids.filter(id => id !== u.id)
                                          : [...ids, u.id]
                                      };
                                    })}
                                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-all ${
                                      isSelected
                                        ? "bg-cyan-500/15 border border-cyan-500/40 text-cyan-300"
                                        : "bg-white/5 border border-white/10 text-slate-400 hover:bg-white/8"
                                    }`}
                                  >
                                    <div className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${isSelected ? "bg-cyan-500 border-cyan-400" : "border-white/20"}`}>
                                      {isSelected && <Check className="w-3 h-3 text-white" />}
                                    </div>
                                    <span className="font-medium">{u.name || u.email}</span>
                                    <span className="text-slate-500 ml-auto">{u.role}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      className="flex-1 border-white/10 text-slate-400"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>

                    <Button
                      type="submit"
                      className="flex-1 bg-arandu-blue hover:bg-arandu-blue-dark text-white"
                      data-testid="save-user-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {editingId ? "Guardar Cambios" : "Crear Usuario"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-4">
          <ChipSearch
            chips={searchChips}
            setChips={setSearchChips}
            inputVal={searchInput}
            setInputVal={setSearchInput}
            placeholder="Buscar usuario, email, rol, cliente, empresa o permiso... Enter para agregar"
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="text-arandu-blue animate-pulse">Cargando...</div>
          </div>
        ) : filteredUsuarios.length === 0 ? (
          <div className="text-center py-12 bg-arandu-dark-light rounded-xl border border-white/5">
            <Users className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">{usuarios.length ? "No hay usuarios que coincidan" : "No hay usuarios registrados"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsuarios.map((usuario, index) => (
              <motion.div
                key={usuario.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden ${usuario.activo === false ? "opacity-60" : ""}`}
                data-testid={`user-card-${usuario.id}`}
              >
                <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        usuario.role === "admin" ? "bg-arandu-red/20"
                        : usuario.role === "gerente" ? "bg-amber-500/20"
                        : "bg-arandu-blue/20"
                      }`}
                    >
                      {usuario.role === "admin" ? (
                        <Shield className="w-5 h-5 text-arandu-red" />
                      ) : usuario.role === "gerente" ? (
                        <UserCog className="w-5 h-5 text-amber-400" />
                      ) : (
                        <Lock className="w-5 h-5 text-arandu-blue" />
                      )}
                    </div>

                    <div>
                      <h3 className="text-white font-medium">{usuario.name}</h3>
                      <p className="text-slate-400 text-sm">{usuario.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        usuario.role === "admin"
                          ? "bg-arandu-red/20 text-arandu-red border border-arandu-red/30"
                          : usuario.role === "gerente"
                          ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                          : "bg-arandu-blue/20 text-arandu-blue border border-arandu-blue/30"
                      }`}
                    >
                      {usuario.role === "admin" ? "Super Admin"
                       : usuario.role === "gerente" ? "Administrador"
                       : "Usuario"}
                    </span>

                    {/* Badge activo/inactivo */}
                    {usuario.activo === false ? (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/20">Inactivo</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/15 text-green-400 border border-green-500/20">Activo</span>
                    )}

                    {usuario.role === "usuario" && (
                      <button
                        onClick={() => setExpandedUser(expandedUser === usuario.id ? null : usuario.id)}
                        className="text-slate-400 hover:text-white transition-colors"
                        data-testid={`expand-user-${usuario.id}`}
                      >
                        {expandedUser === usuario.id ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    )}

                    <div className="flex gap-1">
                      {canManageUser(usuario) && (
                        <Button
                          onClick={() => handleEdit(usuario)}
                          variant="ghost"
                          className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                          title="Editar"
                          data-testid={`edit-user-${usuario.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}

                      {canManageUser(usuario) && usuario.id !== currentUser?.id && (
                        <Button
                          onClick={() => handleToggleActivo(usuario)}
                          variant="ghost"
                          className={usuario.activo === false
                            ? "text-green-400 hover:text-green-300 hover:bg-green-500/10"
                            : "text-slate-400 hover:text-yellow-400 hover:bg-yellow-500/10"}
                          title={usuario.activo === false ? "Activar usuario" : "Inactivar usuario"}
                        >
                          {usuario.activo === false
                            ? <UserCheck className="w-4 h-4" />
                            : <UserX className="w-4 h-4" />}
                        </Button>
                      )}

                      {canImpersonateUser(usuario) && (
                        <Button
                          onClick={() => ingresarComoUsuario(usuario)}
                          variant="ghost"
                          className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                          title="Ingresar como este usuario"
                        >
                          <LogIn className="w-4 h-4" />
                        </Button>
                      )}

                      {canManageUser(usuario) && (
                        <Button
                          onClick={() => handleDelete(usuario.id)}
                          variant="ghost"
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          title="Eliminar"
                          data-testid={`delete-user-${usuario.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedUser === usuario.id && usuario.role === "usuario" && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/5 overflow-hidden"
                    >
                      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Nuestras Empresas */}
                        <div>
                          <p className="text-slate-500 text-xs mb-2 flex items-center gap-1">
                            <Star className="w-3 h-3" /> Nuestras Empresas
                          </p>
                          <p className="text-amber-300 text-sm">
                            {getLogosSummary(usuario.logos_asignados)}
                          </p>
                        </div>

                        {/* Clientes */}
                        <div>
                          <p className="text-slate-500 text-xs mb-2 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Clientes
                          </p>
                          <p className="text-purple-300 text-sm">
                            {getEmpresasSummary(usuario)}
                          </p>
                        </div>

                        {/* Permisos */}
                        <div>
                          <p className="text-slate-500 text-xs mb-2 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Permisos
                          </p>

                          {(usuario.permisos || []).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {(usuario.permisos || []).map(p => (
                                <span
                                  key={p}
                                  className="px-2 py-0.5 bg-arandu-blue/10 border border-arandu-blue/20 rounded text-arandu-blue text-xs"
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-600 text-sm">Sin permisos asignados</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsuariosPage;
